/**
 * device/apps/wardrobe.ts — Dress How You Feel
 *
 * A quick outfit-and-appearance changer that lives on the hPhone/hPad.
 * Five tabs — Hair, Tops, Bottoms, Shoes, Accessories — each showing
 * a scrollable grid of available options as colored tile previews.
 * Tapping an option equips it immediately. The currently worn piece
 * gets a glowing highlight so you always know where you are.
 *
 * Everything is drawn procedurally on a Canvas 2D context. No DOM,
 * no external textures, no dependencies beyond the shared app primitives.
 *
 * "You are already dressed. This just lets you decide in what."
 */

import type { GameState, CharacterAppearance, EquipmentSlot, InventoryItem } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";
import {
  renderLayerPreview,
  SKIN_TONES,
  HAIR_COLORS,
  EYE_COLORS,
  LIP_COLORS,
  MAKEUP_COLORS,
  ITEM_COLORS,
  type CharacterAppearance as ComposerAppearance,
  type Direction as ComposerDirection,
} from "../../character/composer.js";
import { toComposerAppearance, patchComposerAppearance } from "../../render/bridge.js";

// ─── Catalog ────────────────────────────────────────────────────────────────

type TabId = "body" | "face" | "hair" | "tops" | "bottoms" | "shoes" | "accessories" | "gear";

interface TabDef {
  id: TabId;
  label: string;
  /** Key on CharacterAppearance that stores the selected item */
  appearanceKey: keyof CharacterAppearance;
  /** Key on CharacterAppearance that stores the item colour (if any) */
  colourKey?: keyof CharacterAppearance;
  /** Key on the composer's CharacterAppearance (numeric indices) */
  composerKey?: keyof ComposerAppearance;
  options: string[];
}

const TABS: TabDef[] = [
  {
    id: "body",
    label: "Body",
    appearanceKey: "bodyType" as keyof CharacterAppearance,
    options: ["clothed", "nude"],
  },
  {
    id: "face",
    label: "Face",
    appearanceKey: "bodyType" as keyof CharacterAppearance,
    options: [],
  },
  {
    id: "hair",
    label: "Hair",
    appearanceKey: "hairStyle",
    colourKey: "hairColour",
    composerKey: "hairStyle",
    options: [
      "none",
      "crew_cut", "side_part", "afro", "long_straight",
      "messy_bedhead", "wavy_long", "undercut", "ponytail",
      "bob", "long_curly", "spiky_punk", "side_sweep",
      "mohawk", "braids", "top_knot", "layered",
      "pixie_cut", "curtain_bangs", "wolf_cut", "space_buns",
      "half_up", "cornrows", "shag", "high_ponytail",
    ],
  },
  {
    id: "tops",
    label: "Tops",
    appearanceKey: "outfitTop",
    colourKey: "outfitTopColour",
    composerKey: "topStyle",
    options: [
      "none", "t_shirt", "tank_top", "hoodie", "blouse", "button_up",
    ],
  },
  {
    id: "bottoms",
    label: "Btms",
    appearanceKey: "outfitBottom",
    colourKey: "outfitBottomColour",
    composerKey: "bottomStyle",
    options: [
      "none", "jeans", "sweats", "skirt", "shorts",
    ],
  },
  {
    id: "shoes",
    label: "Shoes",
    appearanceKey: "shoes",
    colourKey: "shoesColour",
    composerKey: "shoeStyle",
    options: [
      "none", "sneakers", "boots", "heels", "sandals",
    ],
  },
  {
    id: "accessories",
    label: "Acc.",
    appearanceKey: "accessory",
    colourKey: "accessoryColour",
    composerKey: "accessoryStyle",
    options: [
      "none", "scarf", "glasses", "earrings", "necklace", "bandana",
    ],
  },
  {
    id: "gear",
    label: "Gear",
    appearanceKey: "heldItem" as keyof CharacterAppearance,  // dummy — gear tab uses custom rendering
    options: [],
  },
];

// ─── Per-App State ──────────────────────────────────────────────────────────

/** Compass directions for the rotatable character preview */
const PREVIEW_DIRS: readonly ComposerDirection[] = ["s", "sw", "w", "nw", "n", "ne", "e", "se"];

interface WardrobeState {
  activeTab: TabId;
  scrollOffset: number;
  /** Momentum velocity (px/s) — decays each frame for inertia scroll */
  scrollVelocity: number;
  elapsed: number;
  /** Cached content rect for tap hit-testing (set each frame during draw) */
  cachedRect: { x: number; y: number; w: number; h: number };
  /** Max scroll computed during draw */
  maxScroll: number;
  /** Currently selected outfit slot index for color editing (-1 = none) */
  selectedSlot: number;
  /** Preview rotation index (0-7 into PREVIEW_DIRS) — tap preview to rotate */
  previewDir: number;

  // ── Undo history ──
  /** Ring buffer of ComposerAppearance snapshots. Max 20 entries. */
  history: ComposerAppearance[];
  /** Snapshot taken when wardrobe was opened — for "revert session" */
  sessionStart: ComposerAppearance | null;

  // ── Micro-interaction state ──
  /** Currently pressed control key + decay timer (0.12 → 0). */
  pressed: { key: string; t: number } | null;
  /** Tap ripple origin + lifetime (seconds, 0 → 0.4). */
  ripple: { x: number; y: number; t: number } | null;
  /** Equip flash overlay intensity (decays exponentially). */
  flashT: number;

  // ── Tab transition animation ──
  /** Previous tab id during slide; null when idle. */
  prevTab: TabId | null;
  /** Transition progress 1 → 0 over ~220ms. */
  tabTransT: number;
  /** Direction of slide: +1 = new tab from right, -1 = from left. */
  tabTransDir: number;

  // ── Saved outfit presets ──
  /** Six slots capable of storing a full ComposerAppearance snapshot. */
  savedOutfits: (ComposerAppearance | null)[];

  // ── Entrance animation ──
  /** Open progress 0 → 1 over ~320ms. */
  openT: number;
}

function getState(state: GameState): WardrobeState {
  if (!state.player.device.appStates["wardrobe"]) {
    state.player.device.appStates["wardrobe"] = {
      activeTab: "hair",
      scrollOffset: 0,
      scrollVelocity: 0,
      elapsed: 0,
      cachedRect: { x: 0, y: 0, w: 172, h: 240 },
      maxScroll: 0,
      selectedSlot: -1,
      previewDir: 0,
      history: [],
      sessionStart: null,
      pressed: null,
      ripple: null,
      flashT: 0,
      prevTab: null,
      tabTransT: 0,
      tabTransDir: 1,
      savedOutfits: [null, null, null, null, null, null],
      openT: 0,
    } satisfies WardrobeState;
  }
  const ws = state.player.device.appStates["wardrobe"] as WardrobeState;
  // Back-compat for older persisted states that lack the new fields.
  if (!ws.history) ws.history = [];
  if (ws.sessionStart === undefined) ws.sessionStart = null;
  if (ws.pressed === undefined) ws.pressed = null;
  if (ws.ripple === undefined) ws.ripple = null;
  if (ws.flashT === undefined) ws.flashT = 0;
  if (ws.prevTab === undefined) ws.prevTab = null;
  if (ws.tabTransT === undefined) ws.tabTransT = 0;
  if (ws.tabTransDir === undefined) ws.tabTransDir = 1;
  if (!ws.savedOutfits) ws.savedOutfits = [null, null, null, null, null, null];
  if (ws.openT === undefined) ws.openT = 0;
  return ws;
}

// ─── Interaction Helpers ───────────────────────────────────────────────────
//
// These helpers power every micro-interaction: undo history, press feedback,
// tap ripple, and equip flash. They're all cheap to call and are invoked from
// every tap handler so the wardrobe feels alive.

/** Push a snapshot of the current composer appearance onto the undo ring buffer. */
function pushHistory(ws: WardrobeState): void {
  const snap = toComposerAppearance();
  // Skip duplicates (e.g. tapping the same thing twice)
  const last = ws.history[ws.history.length - 1];
  if (last && JSON.stringify(last) === JSON.stringify(snap)) return;
  ws.history.push(snap);
  // Ring buffer — cap at 20 entries
  while (ws.history.length > 20) ws.history.shift();
}

/** Undo to the previous snapshot. Returns true if undo happened. */
function undoHistory(ws: WardrobeState): boolean {
  if (ws.history.length === 0) return false;
  const prev = ws.history.pop()!;
  setComposerAppearanceFull(prev);
  return true;
}

/** Apply a full ComposerAppearance — bumps version counter, invalidates sprite cache. */
function setComposerAppearanceFull(app: ComposerAppearance): void {
  patchComposerAppearance(app);
}

/** Trigger tap ripple + press feedback + equip flash at a touch point. */
function pushEquipEffect(ws: WardrobeState, x: number, y: number): void {
  ws.ripple = { x, y, t: 0 };
  ws.flashT = 0.18;
}

/** Record a press on a named control. Drawn as a subtle scale/darken for 120ms. */
function pressControl(ws: WardrobeState, key: string): void {
  ws.pressed = { key, t: 0.14 };
}

/** Queue a UI sound — the engine loop flushes ws.ui.uiSounds into the audio engine. */
function playUiSound(state: GameState, sound: string): void {
  state.ui.uiSounds.push(sound);
}

/**
 * Apply a single-key appearance edit with the full audio/visual feedback
 * stack: history snapshot, patch, tap ripple + equip flash, press-state
 * scale, and a UI sound. This is the "one true path" for every chip / slider
 * / palette tap inside the body and face panels so nothing is silent.
 */
function applyEdit(
  ws: WardrobeState, state: GameState,
  localX: number, localY: number,
  patch: Partial<ComposerAppearance>,
  pressKey: string,
  sound: string = "ui_click",
): void {
  pushHistory(ws);
  patchComposerAppearance(patch as any);
  pushEquipEffect(ws, localX, localY);
  pressControl(ws, pressKey);
  playUiSound(state, sound);
}

/**
 * Draw every post-process overlay: tap ripple, equip flash, entrance fade.
 * Called once per frame at the very end of drawContent, after all tab content.
 * Effects are additive and draw on top of everything — they're pure polish.
 */
function drawOverlayEffects(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  ws: WardrobeState,
): void {
  // ── Entrance fade ──────────────────────────────────────────────────
  // openT 0 → 1 over 320ms. Fade alpha + tiny vertical slide.
  if (ws.openT < 1) {
    const t = ws.openT;
    const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
    // Dim overlay fades out as the panel settles
    ctx.fillStyle = `rgba(0,0,0,${(1 - ease) * 0.55})`;
    ctx.fillRect(x, y, w, h);
  }

  // ── Tap ripple ─────────────────────────────────────────────────────
  if (ws.ripple) {
    const t = ws.ripple.t / 0.45; // 0 → 1
    const radius = 8 + t * 48;
    const alpha = (1 - t) * 0.45;
    ctx.save();
    ctx.strokeStyle = `rgba(236,72,153,${alpha})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x + ws.ripple.x, y + ws.ripple.y, radius, 0, Math.PI * 2);
    ctx.stroke();
    // Secondary faster ring for a richer effect
    const r2 = 4 + t * 28;
    ctx.strokeStyle = `rgba(244,63,94,${alpha * 0.7})`;
    ctx.beginPath();
    ctx.arc(x + ws.ripple.x, y + ws.ripple.y, r2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // ── Equip flash ────────────────────────────────────────────────────
  if (ws.flashT > 0) {
    ctx.fillStyle = `rgba(255,255,255,${ws.flashT * 0.14})`;
    ctx.fillRect(x, y, w, h);
  }

  // ── Tab transition slide ───────────────────────────────────────────
  // (Rendered elsewhere as a full-panel shear during the transition —
  // this helper only draws a brief directional highlight bar.)
  if (ws.tabTransT > 0) {
    const t = 1 - ws.tabTransT; // 0 → 1
    const ease = 1 - Math.pow(1 - t, 3);
    const barX = ws.tabTransDir > 0
      ? x + ease * w - 20
      : x + w - ease * w - 20;
    const grad = ctx.createLinearGradient(barX, 0, barX + 40, 0);
    grad.addColorStop(0, "rgba(236,72,153,0)");
    grad.addColorStop(0.5, `rgba(236,72,153,${(1 - ease) * 0.3})`);
    grad.addColorStop(1, "rgba(236,72,153,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(barX, y + SUMMARY_H, 40, TAB_BAR_H);
  }
}

/** Randomize every composer field respecting gender/nude constraints. */
function randomizeAppearance(current: ComposerAppearance): ComposerAppearance {
  const r = (n: number) => Math.floor(Math.random() * n);
  const maybe = (v: number, p: number) => Math.random() < p ? v : -1;
  const gender = current.bodyGender;
  return {
    ...current,
    skinTone:        r(SKIN_TONES.length),
    hairStyle:       maybe(r(24), 0.92),
    hairColor:       r(HAIR_COLORS.length),
    eyeStyle:        r(8),
    eyeColor:        r(EYE_COLORS.length),
    topStyle:        r(5),
    topColor:        r(ITEM_COLORS.length),
    bottomStyle:     r(4),
    bottomColor:     r(ITEM_COLORS.length),
    shoeStyle:       r(4),
    shoeColor:       r(ITEM_COLORS.length),
    hatStyle:        maybe(r(8), 0.35),
    hatColor:        r(ITEM_COLORS.length),
    heldItem:        maybe(r(6), 0.25),
    jacketStyle:     maybe(r(3), 0.4),
    jacketColor:     r(ITEM_COLORS.length),
    skinDetail:      maybe(r(3), 0.25),
    accessoryStyle:  maybe(r(5), 0.35),
    accessoryColor:  r(ITEM_COLORS.length),
    auraStyle:       maybe(r(4), 0.1),
    wingsStyle:      maybe(r(4), 0.08),
    wingsColor:      r(ITEM_COLORS.length),
    capeStyle:       maybe(r(3), 0.1),
    capeColor:       r(ITEM_COLORS.length),
    backpackStyle:   maybe(r(3), 0.15),
    backpackColor:   r(ITEM_COLORS.length),
    facialHairStyle: gender === "masculine" ? maybe(r(4), 0.35) : -1,
    facialHairColor: current.hairColor,
    makeupStyle:     maybe(r(4), 0.3),
    makeupColor:     r(MAKEUP_COLORS.length),
    lipStyle:        maybe(r(3), 0.3),
    lipColor:        r(LIP_COLORS.length),
    expression:      (["neutral","smile","laugh","surprise","sleepy"] as const)[r(5)],
  };
}

// ─── Layout Constants ───────────────────────────────────────────────────────

/** Outfit summary banner at the top */
const SUMMARY_H = 38;

/** Tab bar height */
const TAB_BAR_H = 26;

/** Grid geometry */
const GRID_PAD = 8;
const GRID_GAP = 6;
const CELL_RADIUS = 6;
const LABEL_H = 14;

/** Colors — iOS dark mode palette */
const C_BG_DARK       = "#1c1c1e";
const C_BG_CARD       = "#2c2c2e";
const C_TAB_BG        = "#1c1c1e";
const C_TAB_ACTIVE    = "#ec4899";
const C_TAB_INACTIVE  = "rgba(255,255,255,0.4)";
const C_TEXT_PRIMARY   = "#ffffff";
const C_TEXT_SECONDARY = "rgba(255,255,255,0.55)";
const C_TEXT_DIM       = "rgba(255,255,255,0.35)";
const C_ACCENT         = "#ec4899";
const C_ACCENT_GLOW    = "rgba(236,72,153,0.35)";
const C_EQUIPPED_RING  = "#f43f5e";
const C_CELL_BG        = "#38383a";
const C_SEPARATOR      = "rgba(255,255,255,0.12)";

// ─── Body Shape Labels ─────────────────────────────────────────────────────

const ARCHETYPE_LABELS = ["Athltc", "Soft", "Lithe", "Power", "Petite"] as const;
const ARCHETYPE_IDS: readonly string[] = ["athletic", "soft", "lithe", "powerful", "petite"];
const BLEND_LABELS     = ["Primary", "Mostly 1", "Even", "Mostly 2", "Second"] as const;
const SCALE_LABELS     = ["Slight", "Lean", "Medium", "Full", "Grand"] as const;
const BODY_LABELS      = ["Slim", "Average", "Broad", "Petite"] as const;
const SKIN_DETAIL_LABELS = ["None", "Freckle", "Marks", "Vitilgo"] as const;

// ─── Face Labels ───────────────────────────────────────────────────────────
// Labels abbreviated to fit phone-width chip buttons (max ~7 chars at 25px multi-row chips)

const EYE_SHAPE_LABELS = ["Open", "Serene", "Wide", "Wry", "Spark", "Droopy", "Dot", "Happy"] as const;
const FACIAL_HAIR_LABELS = ["None", "Stubble", "Goatee", "Full B.", "Stache", "Soul P.", "Mutton"] as const;
const MAKEUP_LABELS = ["None", "Naturl", "Glam", "Smoky", "Cat Eye", "Blush", "Avant"] as const;
const LIP_LABELS = ["None", "Subtle", "Matte", "Gloss", "Bold", "Ombré"] as const;
const EXPRESSION_LABELS = ["Neutral", "Smile", "Frown", "Surprse", "Laugh", "Sleepy"] as const;
const EXPRESSION_VALUES: readonly string[] = ["neutral", "smile", "frown", "surprise", "laugh", "sleepy"];

// ─── Extras Labels ─────────────────────────────────────────────────────────

const AURA_LABELS = ["None", "Ember", "Bloom", "Frost", "Pulse"] as const;
const WINGS_LABELS = ["None", "Angel", "B.fly", "Bat", "Fairy"] as const;
const CAPE_LABELS = ["None", "Short", "Long", "Hooded"] as const;
const BACKPACK_LABELS = ["None", "Small", "Hiking", "Satchel"] as const;

// ─── Outfit Slot Style Labels ──────────────────────────────────────────────
// Shown as chip rows when a paper-doll slot is selected in the body panel.
// Hat/Jacket/Held are only editable here — they have no top-level tab.

const HAT_LABELS      = ["None", "Beanie", "Cap", "Top Hat", "Crown", "Witch", "Beret", "Halo", "Bucket"] as const;
const JACKET_LABELS   = ["None", "Cardgn", "Bomber", "Trench"] as const;
/**
 * Held items 0-5 are physical props the player can carry cosmetically.
 * Items 6 (hPhone) and 7 (hPad) are reserved for the device app runtime —
 * they are set/cleared when the device is opened and are deliberately NOT
 * selectable here.
 */
const HELD_LABELS     = ["None", "Coffee", "Phone", "Book", "Flower", "Lantern", "Gift"] as const;
const TOP_STYLE_LABELS    = ["None", "T-Shirt", "Tank", "Hoodie", "Blouse", "Button"] as const;
const BOTTOM_STYLE_LABELS = ["None", "Jeans", "Sweats", "Skirt", "Shorts"] as const;
const SHOE_STYLE_LABELS   = ["None", "Sneaker", "Boots", "Heels", "Sandal"] as const;

/** Label array + count for every paper-doll slot. Used by the style chip row. */
const SLOT_STYLE_LABELS: Record<string, readonly string[]> = {
  hatStyle:    HAT_LABELS,
  jacketStyle: JACKET_LABELS,
  heldItem:    HELD_LABELS,
  topStyle:    TOP_STYLE_LABELS,
  bottomStyle: BOTTOM_STYLE_LABELS,
  shoeStyle:   SHOE_STYLE_LABELS,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Return the color palette for a grid tab, or null if the tab has no color control */
function getTabColorPalette(tab: TabId): readonly string[] | null {
  switch (tab) {
    case "hair": return HAIR_COLORS;
    case "tops": case "bottoms": case "shoes": case "accessories": return ITEM_COLORS;
    default: return null;
  }
}

/** Compute the height needed for the grid tab's bottom color palette bar */
function computeGridPaletteH(paletteLen: number, panelW: number): number {
  const palPad = 4;
  const availW = panelW - palPad * 2;
  const cols = Math.min(paletteLen, Math.floor(availW / (MIN_SWATCH + SWATCH_GAP)));
  const swSz = Math.min(MAX_SWATCH, Math.floor(availW / cols) - SWATCH_GAP);
  const rows = Math.ceil(paletteLen / cols);
  return rows * (swSz + SWATCH_GAP) + palPad * 2;
}

// ─── Character Layer Preview ───────────────────────────────────────────────

/** Map wardrobe tabs to composer style keys and drawable layer IDs */
const TAB_LAYER_MAP: Record<TabId, {
  styleKey: keyof ComposerAppearance;
  colorKey: keyof ComposerAppearance;
  layers: string[];
  maxStyles: number;
}> = {
  body:        { styleKey: "nude" as keyof ComposerAppearance, colorKey: "bodyGender" as keyof ComposerAppearance, layers: ["body", "anatomy"], maxStyles: 2 },
  face:        { styleKey: "eyeStyle",     colorKey: "eyeColor",     layers: ["body", "eyes", "facial-hair", "makeup", "lips"], maxStyles: 8 },
  hair:        { styleKey: "hairStyle",    colorKey: "hairColor",    layers: ["body", "hair-back", "hair-front"],      maxStyles: 24 },
  tops:        { styleKey: "topStyle",     colorKey: "topColor",     layers: ["body", "top"],                          maxStyles: 5  },
  bottoms:     { styleKey: "bottomStyle",  colorKey: "bottomColor",  layers: ["body", "bottom"],                       maxStyles: 4  },
  shoes:       { styleKey: "shoeStyle",    colorKey: "shoeColor",    layers: ["body", "shoes"],                        maxStyles: 4  },
  accessories: { styleKey: "accessoryStyle", colorKey: "accessoryColor", layers: ["body", "accessory"],                maxStyles: 5  },
  gear:        { styleKey: "heldItem" as keyof ComposerAppearance, colorKey: "heldItem" as keyof ComposerAppearance, layers: [], maxStyles: 0 },
};

/** Tiny offscreen canvas for rendering character layer previews */
const PREVIEW_SIZE = 64;
let _previewCanvas: OffscreenCanvas | null = null;
let _previewCtx: OffscreenCanvasRenderingContext2D | null = null;

function getPreviewCtx(): OffscreenCanvasRenderingContext2D | null {
  if (!_previewCanvas) {
    if (typeof OffscreenCanvas === "undefined") return null;
    _previewCanvas = new OffscreenCanvas(PREVIEW_SIZE, PREVIEW_SIZE);
    _previewCtx = _previewCanvas.getContext("2d");
  }
  return _previewCtx;
}

/**
 * Render a character layer preview for a wardrobe item into the target canvas.
 * Returns true if it drew something, false if it fell back.
 */
function drawLayerPreview(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
  itemIndex: number, tab: TabId,
): boolean {
  const pCtx = getPreviewCtx();
  if (!pCtx) return false;

  const info = TAB_LAYER_MAP[tab];
  if (!info) return false;

  // Get current player appearance and override the relevant style
  const base = toComposerAppearance();
  const preview = { ...base };
  (preview as any)[info.styleKey] = itemIndex % info.maxStyles;

  // Clear and render
  pCtx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
  pCtx.save();
  // Character draws around origin — center it in the preview canvas
  pCtx.translate(PREVIEW_SIZE / 2, PREVIEW_SIZE / 2 + 8);
  pCtx.scale(0.7, 0.7);
  renderLayerPreview(pCtx, preview, info.layers, "s", 0);
  pCtx.restore();

  // Blit to target
  const drawScale = size / PREVIEW_SIZE * 0.85;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(drawScale, drawScale);
  ctx.drawImage(_previewCanvas!, -PREVIEW_SIZE / 2, -PREVIEW_SIZE / 2);
  ctx.restore();

  return true;
}

/** "long_wavy" -> "Long Wavy" */
function formatName(raw: string): string {
  return raw
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Deterministic pastel color from a string — used for item tile previews */
function hashColor(str: string): string {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  const hue = ((h % 360) + 360) % 360;
  return `hsl(${hue}, 45%, 55%)`;
}

/** Slightly lighter variant for a gradient top */
function hashColorLight(str: string): string {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  const hue = ((h % 360) + 360) % 360;
  return `hsl(${hue}, 50%, 70%)`;
}

/**
 * Draw a unique, item-specific icon for each wardrobe option.
 * Every hair style, garment, shoe, and accessory has its own recognizable
 * silhouette so the player can tell them apart at a glance.
 */
function drawItemIcon(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
  itemKey: string, tab: TabId,
  itemColor?: string,
): void {
  const s = size * 0.28;
  // Use the item's actual color when available, fallback to white
  const fill = itemColor ? adjustAlpha(itemColor, 0.9) : "rgba(255,255,255,0.88)";
  const stroke = itemColor ? adjustAlpha(itemColor, 0.7) : "rgba(255,255,255,0.65)";
  ctx.fillStyle = fill;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // ── Universal "none" glyph ───────────────────────────────────────────────
  // Every tab can have a "none" option meaning "remove this layer." Render
  // it as a slashed dashed circle — a universal visual grammar for empty.
  // Hair has its own handling because it still needs the head underneath.
  if (itemKey === "none" && tab !== "hair") {
    ctx.save();
    ctx.setLineDash([2, 2]);
    ctx.strokeStyle = "rgba(255,255,255,0.45)";
    ctx.beginPath();
    ctx.arc(cx, cy, s * 0.9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(cx - s * 0.6, cy + s * 0.6);
    ctx.lineTo(cx + s * 0.6, cy - s * 0.6);
    ctx.stroke();
    ctx.restore();
    return;
  }

  // ── Hair styles ─────────────────────────────────────────────────────────
  if (tab === "hair") {
    // Head circle (shared)
    ctx.beginPath();
    ctx.arc(cx, cy + s * 0.1, s * 0.45, 0, Math.PI * 2);
    ctx.stroke();

    switch (itemKey) {
      case "none":
      case "bald":
        // No hair — just a subtle dashed dome to show "empty"
        ctx.save();
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.arc(cx, cy + s * 0.1, s * 0.48, Math.PI, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        break;
      case "crew_cut":
        // Flat, short, shaped tightly to the dome
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.1, s * 0.5, Math.PI * 1.1, Math.PI * 1.9);
        ctx.fill();
        break;
      case "side_part":
        // Swept-over block with a visible part line
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.55, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.save();
        ctx.strokeStyle = "rgba(0,0,0,0.35)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.15, cy - s * 0.6);
        ctx.lineTo(cx - s * 0.05, cy - s * 0.1);
        ctx.stroke();
        ctx.restore();
        break;
      case "afro":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.78, 0, Math.PI * 2);
        ctx.fill();
        // Texture dots
        ctx.save();
        ctx.fillStyle = "rgba(0,0,0,0.2)";
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * s * 0.5, cy - s * 0.15 + Math.sin(a) * s * 0.5, s * 0.08, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
        break;
      case "long_straight":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.6, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.fillRect(cx - s * 0.6, cy, s * 0.35, s * 0.95);
        ctx.fillRect(cx + s * 0.25, cy, s * 0.35, s * 0.95);
        break;
      case "messy_bedhead":
        // Lumpy, irregular silhouette
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.65, cy + s * 0.1);
        ctx.quadraticCurveTo(cx - s * 0.8, cy - s * 0.5, cx - s * 0.2, cy - s * 0.75);
        ctx.quadraticCurveTo(cx - s * 0.05, cy - s * 0.55, cx + s * 0.25, cy - s * 0.7);
        ctx.quadraticCurveTo(cx + s * 0.75, cy - s * 0.45, cx + s * 0.6, cy + s * 0.1);
        ctx.closePath();
        ctx.fill();
        break;
      case "wavy_long":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.6, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        // Wavy side strands
        ctx.lineWidth = s * 0.15;
        for (let i = 0; i < 2; i++) {
          const sx = i === 0 ? cx - s * 0.45 : cx + s * 0.45;
          ctx.beginPath();
          ctx.moveTo(sx, cy);
          ctx.bezierCurveTo(sx + s * 0.25, cy + s * 0.3, sx - s * 0.2, cy + s * 0.55, sx + s * 0.1, cy + s * 0.9);
          ctx.stroke();
        }
        ctx.lineWidth = 1;
        break;
      case "undercut":
        // Top fade with longer top
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.2, s * 0.55, Math.PI * 1.1, Math.PI * 1.9);
        ctx.fill();
        // Shaved sides suggestion
        ctx.save();
        ctx.strokeStyle = "rgba(0,0,0,0.3)";
        ctx.lineWidth = 1;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(cx - s * 0.5, cy - s * 0.05 + i * s * 0.08);
          ctx.lineTo(cx + s * 0.5, cy - s * 0.05 + i * s * 0.08);
          ctx.stroke();
        }
        ctx.restore();
        break;
      case "ponytail":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.55, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx + s * 0.2, cy - s * 0.6);
        ctx.quadraticCurveTo(cx + s * 0.8, cy - s * 0.4, cx + s * 0.5, cy + s * 0.6);
        ctx.lineWidth = s * 0.2;
        ctx.stroke();
        ctx.lineWidth = 1;
        ctx.fillRect(cx + s * 0.15, cy - s * 0.55, s * 0.15, s * 0.1);
        break;
      case "bob":
        // Clean chin-length bob
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.65, cy - s * 0.1);
        ctx.quadraticCurveTo(cx - s * 0.7, cy - s * 0.7, cx, cy - s * 0.75);
        ctx.quadraticCurveTo(cx + s * 0.7, cy - s * 0.7, cx + s * 0.65, cy - s * 0.1);
        ctx.lineTo(cx + s * 0.55, cy + s * 0.25);
        ctx.lineTo(cx - s * 0.55, cy + s * 0.25);
        ctx.closePath();
        ctx.fill();
        break;
      case "long_curly":
        // Wide, curly silhouette built from circles
        for (let i = 0; i < 10; i++) {
          const a = Math.PI + (i / 9) * Math.PI;
          const r = s * 0.68;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * r, cy - s * 0.1 + Math.sin(a) * r, s * 0.22, 0, Math.PI * 2);
          ctx.fill();
        }
        // Body
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.05, s * 0.55, 0, Math.PI * 2);
        ctx.fill();
        break;
      case "spiky_punk":
        // Sharp spikes radiating up
        for (let i = 0; i < 7; i++) {
          const bx = cx - s * 0.5 + (i / 6) * s;
          ctx.beginPath();
          ctx.moveTo(bx - s * 0.06, cy - s * 0.1);
          ctx.lineTo(bx, cy - s * 0.85 + Math.abs(i - 3) * s * 0.08);
          ctx.lineTo(bx + s * 0.06, cy - s * 0.1);
          ctx.closePath();
          ctx.fill();
        }
        break;
      case "side_sweep":
        // Asymmetric sweep across forehead
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.55, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.55, cy - s * 0.55);
        ctx.quadraticCurveTo(cx + s * 0.1, cy - s * 0.3, cx + s * 0.6, cy - s * 0.05);
        ctx.quadraticCurveTo(cx + s * 0.2, cy - s * 0.5, cx - s * 0.55, cy - s * 0.55);
        ctx.closePath();
        ctx.fill();
        break;
      case "mohawk":
        for (let i = 0; i < 5; i++) {
          ctx.beginPath();
          ctx.moveTo(cx - s * 0.15 + i * s * 0.08, cy - s * 0.1);
          ctx.lineTo(cx - s * 0.05 + i * s * 0.08, cy - s * 0.8 + Math.abs(i - 2) * s * 0.15);
          ctx.lineTo(cx + s * 0.05 + i * s * 0.08, cy - s * 0.1);
          ctx.fill();
        }
        break;
      case "braids":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.55, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        for (const side of [-1, 1]) {
          for (let j = 0; j < 4; j++) {
            ctx.fillRect(cx + side * s * 0.3 - s * 0.08, cy + j * s * 0.2, s * 0.16, s * 0.12);
          }
        }
        break;
      case "top_knot":
        // Knot above the head
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.1, s * 0.5, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.75, s * 0.28, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.strokeStyle = "rgba(0,0,0,0.3)";
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.75, s * 0.28, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        break;
      case "layered":
        // Multi-tier silhouette
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.58, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.65, cy);
        ctx.quadraticCurveTo(cx - s * 0.5, cy + s * 0.25, cx - s * 0.3, cy + s * 0.4);
        ctx.lineTo(cx + s * 0.3, cy + s * 0.4);
        ctx.quadraticCurveTo(cx + s * 0.5, cy + s * 0.25, cx + s * 0.65, cy);
        ctx.closePath();
        ctx.fill();
        break;
      case "pixie_cut":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.1, s * 0.52, Math.PI * 0.9, Math.PI * 2.1);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx + s * 0.1, cy - s * 0.6);
        ctx.quadraticCurveTo(cx + s * 0.7, cy - s * 0.4, cx + s * 0.5, cy);
        ctx.fill();
        break;
      case "curtain_bangs":
        // Center-parted bangs framing the face
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.58, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        // Left curtain
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.05, cy - s * 0.6);
        ctx.quadraticCurveTo(cx - s * 0.5, cy - s * 0.3, cx - s * 0.35, cy + s * 0.05);
        ctx.quadraticCurveTo(cx - s * 0.15, cy - s * 0.2, cx - s * 0.05, cy - s * 0.6);
        ctx.closePath();
        ctx.fill();
        // Right curtain (mirror)
        ctx.beginPath();
        ctx.moveTo(cx + s * 0.05, cy - s * 0.6);
        ctx.quadraticCurveTo(cx + s * 0.5, cy - s * 0.3, cx + s * 0.35, cy + s * 0.05);
        ctx.quadraticCurveTo(cx + s * 0.15, cy - s * 0.2, cx + s * 0.05, cy - s * 0.6);
        ctx.closePath();
        ctx.fill();
        break;
      case "wolf_cut":
        // Shaggy top, longer layers at the back
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.7, cy + s * 0.3);
        ctx.quadraticCurveTo(cx - s * 0.8, cy - s * 0.7, cx, cy - s * 0.75);
        ctx.quadraticCurveTo(cx + s * 0.8, cy - s * 0.7, cx + s * 0.7, cy + s * 0.3);
        ctx.lineTo(cx + s * 0.55, cy + s * 0.65);
        ctx.lineTo(cx + s * 0.2, cy + s * 0.5);
        ctx.lineTo(cx - s * 0.2, cy + s * 0.65);
        ctx.lineTo(cx - s * 0.55, cy + s * 0.5);
        ctx.closePath();
        ctx.fill();
        break;
      case "space_buns":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.05, s * 0.45, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        // Two buns
        ctx.beginPath();
        ctx.arc(cx - s * 0.45, cy - s * 0.55, s * 0.22, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx + s * 0.45, cy - s * 0.55, s * 0.22, 0, Math.PI * 2);
        ctx.fill();
        break;
      case "half_up":
        // Hair down with a top-half lifted
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.58, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.fillRect(cx - s * 0.5, cy - s * 0.05, s, s * 0.7);
        // Top pulled-back loop
        ctx.save();
        ctx.strokeStyle = "rgba(0,0,0,0.35)";
        ctx.lineWidth = s * 0.08;
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.55, s * 0.2, 0, Math.PI);
        ctx.stroke();
        ctx.restore();
        break;
      case "cornrows":
        // Parallel rows running back
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.1, s * 0.5, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        ctx.save();
        ctx.strokeStyle = "rgba(0,0,0,0.45)";
        ctx.lineWidth = 1;
        for (let i = 0; i < 5; i++) {
          const lx = cx - s * 0.4 + (i / 4) * s * 0.8;
          ctx.beginPath();
          ctx.moveTo(lx, cy - s * 0.55);
          ctx.lineTo(lx, cy - s * 0.05);
          ctx.stroke();
        }
        ctx.restore();
        break;
      case "shag":
        // Layered, textured all-around
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.62, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        for (let i = 0; i < 6; i++) {
          const lx = cx - s * 0.55 + (i / 5) * s * 1.1;
          ctx.beginPath();
          ctx.moveTo(lx, cy);
          ctx.lineTo(lx - s * 0.04, cy + s * 0.4 + Math.sin(i) * s * 0.1);
          ctx.lineWidth = s * 0.14;
          ctx.stroke();
        }
        ctx.lineWidth = 1;
        break;
      case "high_ponytail":
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.05, s * 0.5, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
        // High tail arc
        ctx.lineWidth = s * 0.22;
        ctx.beginPath();
        ctx.moveTo(cx, cy - s * 0.5);
        ctx.bezierCurveTo(cx + s * 0.5, cy - s * 0.9, cx + s * 0.85, cy - s * 0.4, cx + s * 0.7, cy + s * 0.3);
        ctx.stroke();
        ctx.lineWidth = 1;
        // Band
        ctx.fillRect(cx - s * 0.1, cy - s * 0.55, s * 0.2, s * 0.12);
        break;
      default:
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.15, s * 0.55, Math.PI * 1.0, Math.PI * 2.0);
        ctx.fill();
    }
    return;
  }

  // ── Tops ────────────────────────────────────────────────────────────────
  if (tab === "tops") {
    switch (itemKey) {
      case "cozy_sweater":
        // Wide, cozy with ribbed bottom
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.35, cy - s * 0.85);
        ctx.lineTo(cx - s, cy - s * 0.4);
        ctx.lineTo(cx - s * 0.65, cy - s * 0.2);
        ctx.lineTo(cx - s * 0.65, cy + s * 0.8);
        ctx.lineTo(cx + s * 0.65, cy + s * 0.8);
        ctx.lineTo(cx + s * 0.65, cy - s * 0.2);
        ctx.lineTo(cx + s, cy - s * 0.4);
        ctx.lineTo(cx + s * 0.35, cy - s * 0.85);
        ctx.closePath();
        ctx.fill();
        // Ribbing lines
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(cx - s * 0.55, cy + s * 0.55 + i * s * 0.08);
          ctx.lineTo(cx + s * 0.55, cy + s * 0.55 + i * s * 0.08);
          ctx.strokeStyle = "rgba(255,255,255,0.3)";
          ctx.stroke();
        }
        ctx.strokeStyle = "rgba(255,255,255,0.65)";
        break;
      case "t_shirt":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.4, cy - s);
        ctx.lineTo(cx - s, cy - s * 0.4);
        ctx.lineTo(cx - s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx - s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx + s, cy - s * 0.4);
        ctx.lineTo(cx + s * 0.4, cy - s);
        ctx.closePath();
        ctx.fill();
        break;
      case "tank_top":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.25, cy - s * 0.9);
        ctx.lineTo(cx - s * 0.5, cy - s * 0.3);
        ctx.lineTo(cx - s * 0.5, cy + s);
        ctx.lineTo(cx + s * 0.5, cy + s);
        ctx.lineTo(cx + s * 0.5, cy - s * 0.3);
        ctx.lineTo(cx + s * 0.25, cy - s * 0.9);
        ctx.closePath();
        ctx.fill();
        break;
      case "hoodie":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.4, cy - s);
        ctx.lineTo(cx - s * 1.05, cy - s * 0.3);
        ctx.lineTo(cx - s * 0.7, cy - s * 0.1);
        ctx.lineTo(cx - s * 0.7, cy + s);
        ctx.lineTo(cx + s * 0.7, cy + s);
        ctx.lineTo(cx + s * 0.7, cy - s * 0.1);
        ctx.lineTo(cx + s * 1.05, cy - s * 0.3);
        ctx.lineTo(cx + s * 0.4, cy - s);
        ctx.closePath();
        ctx.fill();
        // Hood
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.85, s * 0.35, Math.PI * 0.15, Math.PI * 0.85);
        ctx.stroke();
        // Pocket
        ctx.fillStyle = "rgba(255,255,255,0.2)";
        ctx.fillRect(cx - s * 0.35, cy + s * 0.3, s * 0.7, s * 0.3);
        ctx.fillStyle = "rgba(255,255,255,0.88)";
        break;
      case "blazer":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.3, cy - s);
        ctx.lineTo(cx - s * 0.85, cy - s * 0.45);
        ctx.lineTo(cx - s * 0.65, cy - s * 0.25);
        ctx.lineTo(cx - s * 0.65, cy + s);
        ctx.lineTo(cx + s * 0.65, cy + s);
        ctx.lineTo(cx + s * 0.65, cy - s * 0.25);
        ctx.lineTo(cx + s * 0.85, cy - s * 0.45);
        ctx.lineTo(cx + s * 0.3, cy - s);
        ctx.closePath();
        ctx.fill();
        // Lapels
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.15, cy - s * 0.85);
        ctx.lineTo(cx - s * 0.3, cy - s * 0.2);
        ctx.moveTo(cx + s * 0.15, cy - s * 0.85);
        ctx.lineTo(cx + s * 0.3, cy - s * 0.2);
        ctx.stroke();
        break;
      case "blouse":
        // Relaxed silhouette with wide neckline
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.3, cy - s * 0.85);
        ctx.lineTo(cx - s * 0.9, cy - s * 0.3);
        ctx.lineTo(cx - s * 0.55, cy - s * 0.1);
        ctx.lineTo(cx - s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.55, cy - s * 0.1);
        ctx.lineTo(cx + s * 0.9, cy - s * 0.3);
        ctx.lineTo(cx + s * 0.3, cy - s * 0.85);
        ctx.closePath();
        ctx.fill();
        // Scoop neck
        ctx.beginPath();
        ctx.arc(cx, cy - s * 0.7, s * 0.25, 0.2 * Math.PI, 0.8 * Math.PI);
        ctx.stroke();
        break;
      case "button_up":
        // Collared shirt with button placket
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.35, cy - s);
        ctx.lineTo(cx - s * 0.85, cy - s * 0.4);
        ctx.lineTo(cx - s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx - s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx + s * 0.85, cy - s * 0.4);
        ctx.lineTo(cx + s * 0.35, cy - s);
        ctx.closePath();
        ctx.fill();
        // Collar points
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.1, cy - s * 0.8);
        ctx.lineTo(cx - s * 0.3, cy - s * 0.55);
        ctx.moveTo(cx + s * 0.1, cy - s * 0.8);
        ctx.lineTo(cx + s * 0.3, cy - s * 0.55);
        ctx.stroke();
        // Buttons
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.arc(cx, cy - s * 0.4 + i * s * 0.4, 1, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      case "crop_top":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.35, cy - s * 0.8);
        ctx.lineTo(cx - s * 0.8, cy - s * 0.25);
        ctx.lineTo(cx - s * 0.55, cy - s * 0.05);
        ctx.lineTo(cx - s * 0.55, cy + s * 0.3);
        ctx.lineTo(cx + s * 0.55, cy + s * 0.3);
        ctx.lineTo(cx + s * 0.55, cy - s * 0.05);
        ctx.lineTo(cx + s * 0.8, cy - s * 0.25);
        ctx.lineTo(cx + s * 0.35, cy - s * 0.8);
        ctx.closePath();
        ctx.fill();
        break;
      case "dress_shirt":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.35, cy - s);
        ctx.lineTo(cx - s * 0.9, cy - s * 0.4);
        ctx.lineTo(cx - s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx - s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx + s * 0.9, cy - s * 0.4);
        ctx.lineTo(cx + s * 0.35, cy - s);
        ctx.closePath();
        ctx.fill();
        // Collar
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.15, cy - s * 0.9);
        ctx.lineTo(cx - s * 0.35, cy - s * 0.55);
        ctx.lineTo(cx, cy - s * 0.65);
        ctx.lineTo(cx + s * 0.35, cy - s * 0.55);
        ctx.lineTo(cx + s * 0.15, cy - s * 0.9);
        ctx.stroke();
        // Buttons
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.arc(cx, cy - s * 0.5 + i * s * 0.35, s * 0.05, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      case "overalls":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.5, cy + s);
        ctx.lineTo(cx - s * 0.5, cy - s * 0.3);
        ctx.lineTo(cx - s * 0.3, cy - s * 0.8);
        ctx.lineTo(cx + s * 0.3, cy - s * 0.8);
        ctx.lineTo(cx + s * 0.5, cy - s * 0.3);
        ctx.lineTo(cx + s * 0.5, cy + s);
        ctx.closePath();
        ctx.fill();
        // Straps
        ctx.lineWidth = s * 0.08;
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.35, cy - s * 0.3);
        ctx.lineTo(cx - s * 0.25, cy - s * 0.75);
        ctx.moveTo(cx + s * 0.35, cy - s * 0.3);
        ctx.lineTo(cx + s * 0.25, cy - s * 0.75);
        ctx.stroke();
        ctx.lineWidth = 1;
        break;
      case "kimono":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.25, cy - s * 0.9);
        ctx.lineTo(cx - s * 0.9, cy - s * 0.1);
        ctx.lineTo(cx - s * 0.7, cy + s);
        ctx.lineTo(cx + s * 0.7, cy + s);
        ctx.lineTo(cx + s * 0.9, cy - s * 0.1);
        ctx.lineTo(cx + s * 0.25, cy - s * 0.9);
        ctx.closePath();
        ctx.fill();
        // Obi sash
        ctx.fillStyle = "rgba(255,255,255,0.35)";
        ctx.fillRect(cx - s * 0.55, cy + s * 0.1, s * 1.1, s * 0.25);
        ctx.fillStyle = "rgba(255,255,255,0.88)";
        break;
      case "leather_jacket":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.35, cy - s);
        ctx.lineTo(cx - s * 1.0, cy - s * 0.35);
        ctx.lineTo(cx - s * 0.7, cy - s * 0.15);
        ctx.lineTo(cx - s * 0.7, cy + s);
        ctx.lineTo(cx + s * 0.7, cy + s);
        ctx.lineTo(cx + s * 0.7, cy - s * 0.15);
        ctx.lineTo(cx + s * 1.0, cy - s * 0.35);
        ctx.lineTo(cx + s * 0.35, cy - s);
        ctx.closePath();
        ctx.fill();
        // Diagonal zip
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.15, cy - s * 0.85);
        ctx.lineTo(cx + s * 0.15, cy + s * 0.4);
        ctx.stroke();
        // Collar flaps
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.35, cy - s * 0.85);
        ctx.lineTo(cx - s * 0.5, cy - s * 0.5);
        ctx.lineTo(cx - s * 0.15, cy - s * 0.6);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(cx + s * 0.35, cy - s * 0.85);
        ctx.lineTo(cx + s * 0.5, cy - s * 0.5);
        ctx.lineTo(cx + s * 0.15, cy - s * 0.6);
        ctx.fill();
        break;
      default:
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.4, cy - s);
        ctx.lineTo(cx - s, cy - s * 0.4);
        ctx.lineTo(cx - s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx - s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy + s);
        ctx.lineTo(cx + s * 0.6, cy - s * 0.2);
        ctx.lineTo(cx + s, cy - s * 0.4);
        ctx.lineTo(cx + s * 0.4, cy - s);
        ctx.closePath();
        ctx.fill();
    }
    return;
  }

  // ── Bottoms ─────────────────────────────────────────────────────────────
  if (tab === "bottoms") {
    switch (itemKey) {
      case "jeans":
      case "trousers":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.7, cy - s);
        ctx.lineTo(cx + s * 0.7, cy - s);
        ctx.lineTo(cx + s * 0.55, cy + s);
        ctx.lineTo(cx + s * 0.1, cy + s);
        ctx.lineTo(cx, cy);
        ctx.lineTo(cx - s * 0.1, cy + s);
        ctx.lineTo(cx - s * 0.55, cy + s);
        ctx.closePath();
        ctx.fill();
        // Pocket stitching
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.45, cy - s * 0.6);
        ctx.lineTo(cx - s * 0.2, cy - s * 0.4);
        ctx.lineTo(cx - s * 0.45, cy - s * 0.25);
        ctx.stroke();
        break;
      case "shorts":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.7, cy - s * 0.8);
        ctx.lineTo(cx + s * 0.7, cy - s * 0.8);
        ctx.lineTo(cx + s * 0.65, cy + s * 0.2);
        ctx.lineTo(cx + s * 0.1, cy + s * 0.2);
        ctx.lineTo(cx, cy - s * 0.1);
        ctx.lineTo(cx - s * 0.1, cy + s * 0.2);
        ctx.lineTo(cx - s * 0.65, cy + s * 0.2);
        ctx.closePath();
        ctx.fill();
        break;
      case "skirt":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.5, cy - s * 0.8);
        ctx.lineTo(cx + s * 0.5, cy - s * 0.8);
        ctx.lineTo(cx + s * 0.85, cy + s);
        ctx.lineTo(cx - s * 0.85, cy + s);
        ctx.closePath();
        ctx.fill();
        break;
      case "sweats":
      case "joggers":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.65, cy - s);
        ctx.lineTo(cx + s * 0.65, cy - s);
        ctx.lineTo(cx + s * 0.45, cy + s * 0.7);
        ctx.lineTo(cx + s * 0.5, cy + s);
        ctx.lineTo(cx + s * 0.1, cy + s);
        ctx.lineTo(cx, cy + s * 0.1);
        ctx.lineTo(cx - s * 0.1, cy + s);
        ctx.lineTo(cx - s * 0.5, cy + s);
        ctx.lineTo(cx - s * 0.45, cy + s * 0.7);
        ctx.closePath();
        ctx.fill();
        // Cuff bands
        ctx.fillStyle = "rgba(255,255,255,0.3)";
        ctx.fillRect(cx - s * 0.5, cy + s * 0.8, s * 0.45, s * 0.12);
        ctx.fillRect(cx + s * 0.1, cy + s * 0.8, s * 0.45, s * 0.12);
        ctx.fillStyle = "rgba(255,255,255,0.88)";
        break;
      default:
        // Generic pants
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.7, cy - s);
        ctx.lineTo(cx + s * 0.7, cy - s);
        ctx.lineTo(cx + s * 0.5, cy + s);
        ctx.lineTo(cx + s * 0.1, cy + s);
        ctx.lineTo(cx, cy);
        ctx.lineTo(cx - s * 0.1, cy + s);
        ctx.lineTo(cx - s * 0.5, cy + s);
        ctx.closePath();
        ctx.fill();
    }
    return;
  }

  // ── Shoes ───────────────────────────────────────────────────────────────
  if (tab === "shoes") {
    switch (itemKey) {
      case "sneakers":
        // Low-profile shoe
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.7, cy + s * 0.2);
        ctx.lineTo(cx - s * 0.7, cy - s * 0.15);
        ctx.lineTo(cx + s * 0.5, cy - s * 0.15);
        ctx.lineTo(cx + s * 0.8, cy + s * 0.05);
        ctx.lineTo(cx + s * 0.8, cy + s * 0.4);
        ctx.lineTo(cx - s * 0.7, cy + s * 0.4);
        ctx.closePath();
        ctx.fill();
        // Sole line
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.7, cy + s * 0.25);
        ctx.lineTo(cx + s * 0.8, cy + s * 0.25);
        ctx.stroke();
        break;
      case "boots":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.5, cy - s * 0.6);
        ctx.lineTo(cx - s * 0.5, cy + s * 0.3);
        ctx.lineTo(cx - s * 0.8, cy + s * 0.3);
        ctx.lineTo(cx - s * 0.8, cy + s * 0.7);
        ctx.lineTo(cx + s * 0.8, cy + s * 0.7);
        ctx.lineTo(cx + s * 0.8, cy + s * 0.3);
        ctx.lineTo(cx + s * 0.2, cy + s * 0.3);
        ctx.lineTo(cx + s * 0.2, cy - s * 0.6);
        ctx.closePath();
        ctx.fill();
        break;
      case "sandals":
        // Flat sole with straps
        ctx.fillRect(cx - s * 0.7, cy + s * 0.15, s * 1.4, s * 0.35);
        // Straps
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.5, cy + s * 0.15);
        ctx.quadraticCurveTo(cx - s * 0.3, cy - s * 0.2, cx, cy + s * 0.15);
        ctx.moveTo(cx, cy + s * 0.15);
        ctx.quadraticCurveTo(cx + s * 0.3, cy - s * 0.2, cx + s * 0.5, cy + s * 0.15);
        ctx.stroke();
        break;
      case "heels":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.4, cy - s * 0.15);
        ctx.lineTo(cx + s * 0.7, cy + s * 0.1);
        ctx.lineTo(cx + s * 0.7, cy + s * 0.35);
        ctx.lineTo(cx - s * 0.6, cy + s * 0.35);
        ctx.lineTo(cx - s * 0.6, cy + s * 0.1);
        ctx.closePath();
        ctx.fill();
        // Heel
        ctx.fillRect(cx - s * 0.6, cy + s * 0.35, s * 0.15, s * 0.35);
        break;
      case "loafers":
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.55, cy);
        ctx.quadraticCurveTo(cx - s * 0.55, cy - s * 0.3, cx, cy - s * 0.2);
        ctx.quadraticCurveTo(cx + s * 0.7, cy - s * 0.1, cx + s * 0.7, cy + s * 0.15);
        ctx.lineTo(cx + s * 0.7, cy + s * 0.4);
        ctx.lineTo(cx - s * 0.55, cy + s * 0.4);
        ctx.closePath();
        ctx.fill();
        break;
      case "slippers":
        ctx.beginPath();
        ctx.ellipse(cx, cy + s * 0.15, s * 0.65, s * 0.3, 0, 0, Math.PI * 2);
        ctx.fill();
        // Fluffy top
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.beginPath();
        ctx.ellipse(cx - s * 0.15, cy - s * 0.05, s * 0.4, s * 0.2, -0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(255,255,255,0.88)";
        break;
      default:
        ctx.beginPath();
        ctx.moveTo(cx - s * 0.5, cy - s * 0.3);
        ctx.lineTo(cx + s * 0.6, cy - s * 0.1);
        ctx.lineTo(cx + s * 0.7, cy + s * 0.4);
        ctx.lineTo(cx - s * 0.6, cy + s * 0.4);
        ctx.closePath();
        ctx.fill();
    }
    return;
  }

  // ── Accessories ─────────────────────────────────────────────────────────
  if (tab === "accessories") {
    if (itemKey === "none") {
      ctx.beginPath();
      ctx.arc(cx, cy, s * 0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.25, cy);
      ctx.lineTo(cx + s * 0.25, cy);
      ctx.stroke();
    } else if (itemKey === "scarf") {
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.4, cy - s * 0.5);
      ctx.quadraticCurveTo(cx + s * 0.6, cy - s * 0.7, cx + s * 0.3, cy);
      ctx.quadraticCurveTo(cx + s * 0.1, cy + s * 0.5, cx - s * 0.2, cy + s * 0.8);
      ctx.lineWidth = s * 0.18;
      ctx.stroke();
      ctx.lineWidth = 1;
    } else if (itemKey === "glasses") {
      ctx.beginPath();
      ctx.arc(cx - s * 0.3, cy, s * 0.25, 0, Math.PI * 2);
      ctx.arc(cx + s * 0.3, cy, s * 0.25, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.05, cy);
      ctx.lineTo(cx + s * 0.05, cy);
      ctx.stroke();
    } else if (itemKey === "watch") {
      ctx.beginPath();
      ctx.arc(cx, cy, s * 0.35, 0, Math.PI * 2);
      ctx.stroke();
      // Band
      ctx.fillRect(cx - s * 0.15, cy - s * 0.7, s * 0.3, s * 0.35);
      ctx.fillRect(cx - s * 0.15, cy + s * 0.35, s * 0.3, s * 0.35);
      // Clock hands
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx, cy - s * 0.2);
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + s * 0.15, cy);
      ctx.stroke();
    } else if (itemKey === "earrings") {
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(cx + side * s * 0.25, cy - s * 0.3);
        ctx.lineTo(cx + side * s * 0.25, cy + s * 0.1);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(cx + side * s * 0.25, cy + s * 0.25, s * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (itemKey === "necklace") {
      ctx.beginPath();
      ctx.arc(cx, cy - s * 0.1, s * 0.5, Math.PI * 0.2, Math.PI * 0.8);
      ctx.stroke();
      // Pendant
      ctx.beginPath();
      ctx.moveTo(cx, cy + s * 0.35);
      ctx.lineTo(cx - s * 0.12, cy + s * 0.6);
      ctx.lineTo(cx + s * 0.12, cy + s * 0.6);
      ctx.closePath();
      ctx.fill();
    } else if (itemKey === "backpack") {
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.4, cy - s * 0.7);
      ctx.lineTo(cx + s * 0.4, cy - s * 0.7);
      ctx.quadraticCurveTo(cx + s * 0.6, cy - s * 0.5, cx + s * 0.5, cy + s * 0.6);
      ctx.lineTo(cx - s * 0.5, cy + s * 0.6);
      ctx.quadraticCurveTo(cx - s * 0.6, cy - s * 0.5, cx - s * 0.4, cy - s * 0.7);
      ctx.fill();
      // Pocket
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.fillRect(cx - s * 0.25, cy, s * 0.5, s * 0.3);
      ctx.fillStyle = "rgba(255,255,255,0.88)";
    } else if (itemKey === "headband") {
      ctx.beginPath();
      ctx.arc(cx, cy - s * 0.1, s * 0.55, Math.PI * 1.05, Math.PI * 1.95);
      ctx.lineWidth = s * 0.12;
      ctx.stroke();
      ctx.lineWidth = 1;
    } else if (itemKey === "bandana") {
      // Tied bandana with knot
      ctx.beginPath();
      ctx.ellipse(cx, cy - s * 0.2, s * 0.55, s * 0.2, 0, 0, Math.PI);
      ctx.fill();
      // Knot
      ctx.beginPath();
      ctx.arc(cx + s * 0.45, cy - s * 0.05, s * 0.12, 0, Math.PI * 2);
      ctx.fill();
      // Trailing tails
      ctx.beginPath();
      ctx.moveTo(cx + s * 0.5, cy);
      ctx.quadraticCurveTo(cx + s * 0.7, cy + s * 0.3, cx + s * 0.4, cy + s * 0.6);
      ctx.moveTo(cx + s * 0.55, cy);
      ctx.quadraticCurveTo(cx + s * 0.8, cy + s * 0.2, cx + s * 0.6, cy + s * 0.5);
      ctx.stroke();
    } else {
      // Generic sparkle star
      const points = 5;
      ctx.beginPath();
      for (let i = 0; i <= points * 2; i++) {
        const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
        const r = i % 2 === 0 ? s * 0.7 : s * 0.3;
        const px = cx + Math.cos(angle) * r;
        const py = cy + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
    }
  }
}

// ─── Grid Geometry Cache ────────────────────────────────────────────────────

interface GridLayout {
  cols: number;
  cellSize: number;
  gridY: number;
  gridW: number;
  gridX: number;
}

function computeGrid(rect: Rect): GridLayout {
  const gridX = rect.x + GRID_PAD;
  const gridW = rect.w - GRID_PAD * 2;
  const gridY = rect.y + SUMMARY_H + TAB_BAR_H + 6;
  const cols = 3;
  const cellSize = Math.floor((gridW - GRID_GAP * (cols - 1)) / cols);
  return { cols, cellSize, gridY, gridW, gridX };
}

// ─── The App ────────────────────────────────────────────────────────────────

const wardrobeApp: DeviceApp = {
  id: "wardrobe",
  name: "Wardrobe",
  iconGradient: ["#ec4899", "#f43f5e"],

  // ── Icon: coat hanger ─────────────────────────────────────────────────

  drawIcon(ctx, cx, cy, size) {
    const s = size * 0.36;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.8;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Hook at top — small curved hook
    ctx.beginPath();
    ctx.arc(cx, cy - s * 0.85, s * 0.2, Math.PI, 0, false);
    ctx.stroke();

    // Triangle body — hanger shoulders
    ctx.beginPath();
    ctx.moveTo(cx, cy - s * 0.65);
    ctx.lineTo(cx - s, cy + s * 0.5);
    ctx.lineTo(cx + s, cy + s * 0.5);
    ctx.closePath();
    ctx.stroke();

    // Horizontal bar at the bottom
    ctx.beginPath();
    ctx.moveTo(cx - s * 1.05, cy + s * 0.5);
    ctx.lineTo(cx + s * 1.05, cy + s * 0.5);
    ctx.stroke();
  },

  // ── Content ───────────────────────────────────────────────────────────

  drawContent(ctx, rect, state, dt) {
    const ws = getState(state);
    ws.elapsed += dt;
    ws.cachedRect = { x: rect.x, y: rect.y, w: rect.w, h: rect.h };
    const appearance = state.player.appearance;
    const { x, y, w, h } = rect;

    const activeTabDef = TABS.find(t => t.id === ws.activeTab)!;
    const equippedValue = ws.activeTab !== "gear"
      ? appearance[activeTabDef.appearanceKey] as string | undefined
      : undefined;

    // ── Outfit Summary Banner ───────────────────────────────────────────
    drawOutfitSummary(ctx, x, y, w, SUMMARY_H, appearance, ws.elapsed, ws);

    // ── Tab Bar ─────────────────────────────────────────────────────────
    const tabY = y + SUMMARY_H;
    drawTabBar(ctx, x, tabY, w, TAB_BAR_H, ws);

    // ── Timer decay for every micro-interaction ──────────────────────
    // Pressed state, tap ripple, equip flash, tab transition, open-in anim
    if (ws.pressed) {
      ws.pressed.t -= dt;
      if (ws.pressed.t <= 0) ws.pressed = null;
    }
    if (ws.ripple) {
      ws.ripple.t += dt;
      if (ws.ripple.t > 0.45) ws.ripple = null;
    }
    if (ws.flashT > 0) {
      ws.flashT *= Math.exp(-8 * dt);
      if (ws.flashT < 0.002) ws.flashT = 0;
    }
    if (ws.tabTransT > 0) {
      ws.tabTransT -= dt / 0.22;
      if (ws.tabTransT <= 0) { ws.tabTransT = 0; ws.prevTab = null; }
    }
    if (ws.openT < 1) {
      ws.openT = Math.min(1, ws.openT + dt / 0.32);
    }

    // ── Momentum / inertia scroll (shared across all tabs) ────────────
    if (Math.abs(ws.scrollVelocity) > 0.5) {
      ws.scrollOffset += ws.scrollVelocity * dt;
      ws.scrollVelocity *= Math.exp(-6 * dt);
    } else {
      ws.scrollVelocity = 0;
    }

    // ── Overscroll rubberband spring ───────────────────────────────────
    // If scrollOffset is outside [0, maxScroll], spring it back toward bounds.
    // This is the iOS rubberband feel — hard clamp would be an Android-2.x list.
    {
      const target = Math.max(0, Math.min(ws.scrollOffset, ws.maxScroll));
      if (ws.scrollOffset !== target) {
        const delta = target - ws.scrollOffset;
        // Critically-damped spring — k=160, d=26 at dt~0.016 settles in ~250ms
        ws.scrollVelocity += delta * 160 * dt;
        ws.scrollVelocity *= Math.exp(-12 * dt);
        if (Math.abs(delta) < 0.3 && Math.abs(ws.scrollVelocity) < 2) {
          ws.scrollOffset = target;
          ws.scrollVelocity = 0;
        }
      }
    }

    // ── Tab Transition Transform ───────────────────────────────────────
    // During tab-change, slide the content panel in from the side and fade
    // it up. tabTransT ∈ [1, 0] over ~220ms. Ease is pow(t, 2) — quadratic,
    // front-loaded motion that settles smoothly. Applied to body/face/gear
    // and the grid path uniformly via save/translate before each branch.
    const transEase = ws.tabTransT * ws.tabTransT;
    const transX = ws.tabTransDir * transEase * w * 0.22;
    const transAlpha = 1 - transEase * 0.65;
    const needsTransform = ws.tabTransT > 0.001;
    if (needsTransform) {
      ctx.save();
      ctx.globalAlpha *= transAlpha;
      ctx.translate(transX, 0);
    }

    // ── Gear Tab: Equipment Display ─────────────────────────────────────
    if (ws.activeTab === "gear") {
      drawEquipmentPanel(ctx, x, tabY + TAB_BAR_H + 4, w, h - SUMMARY_H - TAB_BAR_H - 4, state);
      if (needsTransform) ctx.restore();
      drawOverlayEffects(ctx, x, y, w, h, ws);
      return;
    }

    // ── Body Tab: Paper Doll + Outfit Slots ─────────────────────────────
    if (ws.activeTab === "body") {
      drawBodyPanel(ctx, x, tabY + TAB_BAR_H, w, h - SUMMARY_H - TAB_BAR_H, state, ws);
      if (needsTransform) ctx.restore();
      drawOverlayEffects(ctx, x, y, w, h, ws);
      return;
    }

    // ── Face Tab: Eyes, Expression, Facial Hair, Makeup, Lips ──────────
    if (ws.activeTab === "face") {
      drawFacePanel(ctx, x, tabY + TAB_BAR_H, w, h - SUMMARY_H - TAB_BAR_H, state);
      if (needsTransform) ctx.restore();
      drawOverlayEffects(ctx, x, y, w, h, ws);
      return;
    }

    // ── Grid Area ───────────────────────────────────────────────────────
    const gl = computeGrid(rect);
    const gridAreaTop = gl.gridY;

    // Determine if this tab has a color palette — reserve space at bottom
    const colorPalette = getTabColorPalette(ws.activeTab);
    const paletteBarH = colorPalette ? computeGridPaletteH(colorPalette.length, w) : 0;
    const gridAreaH = y + h - gridAreaTop - paletteBarH;

    // Clip to grid area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, gridAreaTop, w, gridAreaH);
    ctx.clip();

    const options = activeTabDef.options;
    const rows = Math.ceil(options.length / gl.cols);
    const cellTotal = gl.cellSize + GRID_GAP;
    const rowHeight = cellTotal + LABEL_H;
    const totalContentH = rows * rowHeight;
    ws.maxScroll = Math.max(0, totalContentH - gridAreaH + GRID_PAD);

    // NOTE: no hard clamp here — the update loop's rubberband spring handles
    // bounds, allowing iOS-style overscroll with elastic snap-back.

    for (let i = 0; i < options.length; i++) {
      const col = i % gl.cols;
      const row = Math.floor(i / gl.cols);
      const cellX = gl.gridX + col * cellTotal;
      const cellY = gl.gridY + row * (cellTotal + LABEL_H) - ws.scrollOffset;

      // Skip cells outside visible area
      if (cellY + gl.cellSize + LABEL_H < gridAreaTop) continue;
      if (cellY > gridAreaTop + gridAreaH) continue;

      const optionKey = options[i];
      const isEquipped = equippedValue === optionKey;
      const colourValue = activeTabDef.colourKey
        ? (appearance[activeTabDef.colourKey] as string | undefined)
        : undefined;

      drawItemCell(
        ctx, cellX, cellY, gl.cellSize,
        optionKey, activeTabDef.id, isEquipped,
        colourValue, ws.elapsed,
      );
    }

    // Top fade — content scrolls under
    const fadeH = 12;
    const fadeGrad = ctx.createLinearGradient(x, gridAreaTop, x, gridAreaTop + fadeH);
    fadeGrad.addColorStop(0, "rgba(10,10,18,1)");
    fadeGrad.addColorStop(1, "rgba(10,10,18,0)");
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(x, gridAreaTop, w, fadeH);

    // Bottom fade
    const botFadeTop = gridAreaTop + gridAreaH - fadeH;
    const botGrad = ctx.createLinearGradient(x, botFadeTop, x, botFadeTop + fadeH);
    botGrad.addColorStop(0, "rgba(10,10,18,0)");
    botGrad.addColorStop(1, "rgba(10,10,18,1)");
    ctx.fillStyle = botGrad;
    ctx.fillRect(x, botFadeTop, w, fadeH);

    // ── Scroll indicator (right edge) ───────────────────────────────
    if (ws.maxScroll > 0) {
      const trackH = gridAreaH - 8;
      const trackX = x + w - 3;
      const trackY = gridAreaTop + 4;
      const thumbRatio = gridAreaH / (totalContentH || 1);
      const thumbH = Math.max(12, trackH * thumbRatio);
      const scrollRatio = ws.scrollOffset / ws.maxScroll;
      const thumbY = trackY + scrollRatio * (trackH - thumbH);
      ctx.fillStyle = "rgba(255,255,255,0.12)";
      drawRoundRect(ctx, trackX, trackY, 2.5, trackH, 1.25);
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      drawRoundRect(ctx, trackX, thumbY, 2.5, thumbH, 1.25);
      ctx.fill();
    }

    ctx.restore(); // clip

    // ── Color Palette Bar (below grid, fixed) ──────────────────────────
    if (colorPalette) {
      const paletteY = y + h - paletteBarH;
      const composerApp = toComposerAppearance();
      const layerInfo = TAB_LAYER_MAP[ws.activeTab];
      const activeColorIdx = layerInfo ? (composerApp[layerInfo.colorKey] as number) ?? 0 : 0;

      // Background bar
      ctx.fillStyle = "rgba(28,28,30,0.95)";
      ctx.fillRect(x, paletteY, w, paletteBarH);
      ctx.strokeStyle = C_SEPARATOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, paletteY);
      ctx.lineTo(x + w, paletteY);
      ctx.stroke();

      // Draw swatches in multi-row grid
      const palPad = 4;
      const availW = w - palPad * 2;
      const cols = Math.min(colorPalette.length, Math.floor(availW / (MIN_SWATCH + SWATCH_GAP)));
      const swSz = Math.min(MAX_SWATCH, Math.floor(availW / cols) - SWATCH_GAP);
      for (let i = 0; i < colorPalette.length; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const sx = x + palPad + col * (swSz + SWATCH_GAP);
        const sy = paletteY + palPad + row * (swSz + SWATCH_GAP);
        ctx.fillStyle = colorPalette[i];
        drawRoundRect(ctx, sx, sy, swSz, swSz, 2);
        ctx.fill();
        if (i === activeColorIdx) {
          ctx.strokeStyle = C_ACCENT;
          ctx.lineWidth = 1.5;
          drawRoundRect(ctx, sx - 1, sy - 1, swSz + 2, swSz + 2, 3);
          ctx.stroke();
        }
        // Glint — subtle top-edge shine that lifts the swatch off the background
        const glintGrad = ctx.createLinearGradient(sx, sy, sx, sy + swSz * 0.35);
        glintGrad.addColorStop(0, "rgba(255,255,255,0.35)");
        glintGrad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = glintGrad;
        drawRoundRect(ctx, sx, sy, swSz, swSz * 0.35, 2);
        ctx.fill();
      }
    }

    if (needsTransform) ctx.restore();
    drawOverlayEffects(ctx, x, y, w, h, ws);
  },

  // ── Tap ───────────────────────────────────────────────────────────────

  handleTap(localX, localY, state) {
    const ws = getState(state);
    const appearance = state.player.appearance;
    const cr = ws.cachedRect;

    // ── Summary banner action buttons (Undo / Revert / Randomize) ────────
    // Positions mirror drawOutfitSummary. Hit zones are 14×14 px pills.
    if (localY >= 3 && localY < 17) {
      const actionSize = 14;
      const actionGap = 3;
      const randomizeXRel = cr.w - 4 - actionSize;
      const revertXRel    = randomizeXRel - actionGap - actionSize;
      const undoXRel      = revertXRel    - actionGap - actionSize;

      if (localX >= randomizeXRel && localX < randomizeXRel + actionSize) {
        // Randomize — always available
        pushHistory(ws);
        setComposerAppearanceFull(randomizeAppearance(toComposerAppearance()));
        pushEquipEffect(ws, localX, localY);
        pressControl(ws, "btn_random");
        playUiSound(state, "ui_equip");
        return true;
      }
      if (localX >= revertXRel && localX < revertXRel + actionSize) {
        // Revert to session start — only if we have a snapshot
        if (ws.sessionStart) {
          pushHistory(ws);
          setComposerAppearanceFull(ws.sessionStart);
          pushEquipEffect(ws, localX, localY);
          pressControl(ws, "btn_revert");
          playUiSound(state, "ui_click");
        }
        return true;
      }
      if (localX >= undoXRel && localX < undoXRel + actionSize) {
        // Undo — step back in history
        if (undoHistory(ws)) {
          pushEquipEffect(ws, localX, localY);
          pressControl(ws, "btn_undo");
          playUiSound(state, "ui_click");
        }
        return true;
      }
    }

    // ── Tab bar tap ──────────────────────────────────────────────────────
    if (localY >= SUMMARY_H && localY < SUMMARY_H + TAB_BAR_H) {
      const tabCount = TABS.length;
      const perTab = cr.w / tabCount;
      const tappedIdx = Math.min(tabCount - 1, Math.floor(localX / perTab));
      if (tappedIdx >= 0 && tappedIdx < tabCount) {
        const newTab = TABS[tappedIdx].id;
        if (newTab !== ws.activeTab) {
          // Figure out slide direction: tab order in TABS dictates left/right
          const oldIdx = TABS.findIndex(t => t.id === ws.activeTab);
          ws.tabTransDir = tappedIdx > oldIdx ? 1 : -1;
          ws.prevTab = ws.activeTab;
          ws.tabTransT = 1;
          ws.activeTab = newTab;
          ws.scrollOffset = 0;
          ws.scrollVelocity = 0;
          ws.selectedSlot = -1;
          pressControl(ws, `tab_${newTab}`);
          playUiSound(state, "ui_tab");
        }
        return true;
      }
    }

    // ── Gear tab tap ───────────────────────────────────────────────────
    if (ws.activeTab === "gear") {
      return handleGearTap(localX, localY, state, cr);
    }

    // ── Body tab tap — paper doll controls ───────────────────────────────
    if (ws.activeTab === "body") {
      return handleBodyPanelTap(localX, localY, state, cr);
    }

    // ── Face tab tap ─────────────────────────────────────────────────────
    if (ws.activeTab === "face") {
      return handleFacePanelTap(localX, localY, state, cr);
    }

    // ── Grid tap ─────────────────────────────────────────────────────────
    const gridRect: Rect = { x: cr.x, y: cr.y, w: cr.w, h: cr.h };
    const gl = computeGrid(gridRect);
    const gridAreaTop = gl.gridY - cr.y; // relative to content origin

    const relY = localY - gridAreaTop + ws.scrollOffset;
    const relX = localX - (gl.gridX - cr.x);

    const cellTotal = gl.cellSize + GRID_GAP;
    const rowHeight = cellTotal + LABEL_H;

    if (relX >= 0 && relX < gl.gridW && relY >= 0) {
      const col = Math.floor(relX / cellTotal);
      const row = Math.floor(relY / rowHeight);

      if (col >= 0 && col < gl.cols) {
        const activeTabDef = TABS.find(t => t.id === ws.activeTab)!;
        const idx = row * gl.cols + col;

        if (idx >= 0 && idx < activeTabDef.options.length) {
          const chosen = activeTabDef.options[idx];
          const key = activeTabDef.appearanceKey;

          // Equip the item (or clear accessory if "none")
          if (key === "accessory") {
            (appearance as any)[key] = chosen === "none" ? undefined : chosen;
          } else {
            (appearance as any)[key] = chosen;
          }

          // Sync to composer appearance so the change is immediately visible.
          // The option's array index maps to the composer's numeric style index.
          // Accessories prepend "none" at idx 0, so we shift by -1 (scarf at
          // wardrobe idx 1 → composer idx 0). Other tabs may also start with
          // "none" — handle uniformly by checking the chosen value.
          if (activeTabDef.composerKey) {
            let composerVal: number;
            if (chosen === "none") {
              composerVal = -1;
            } else if (activeTabDef.options[0] === "none") {
              // Grid has "none" prepended — shift index down by one
              composerVal = idx - 1;
            } else {
              composerVal = idx;
            }
            pushHistory(ws);
            patchComposerAppearance({ [activeTabDef.composerKey]: composerVal } as any);
          }
          pushEquipEffect(ws, localX, localY);
          pressControl(ws, `grid_${chosen}`);
          playUiSound(state, "ui_equip");
          return true;
        }
      }
    }

    // ── Color palette bar tap (below grid) ────────────────────────────────
    const colorPalette = getTabColorPalette(ws.activeTab);
    if (colorPalette) {
      const palBarH = computeGridPaletteH(colorPalette.length, cr.w);
      const paletteY = cr.h - palBarH;
      if (localY >= paletteY && localY < paletteY + palBarH) {
        const palPad = 4;
        const availW = cr.w - palPad * 2;
        const cols = Math.min(colorPalette.length, Math.floor(availW / (MIN_SWATCH + SWATCH_GAP)));
        const swSz = Math.min(MAX_SWATCH, Math.floor(availW / cols) - SWATCH_GAP);
        const relX = localX - palPad;
        const relY = localY - paletteY - palPad;
        if (relX >= 0 && relY >= 0) {
          const col = Math.floor(relX / (swSz + SWATCH_GAP));
          const row = Math.floor(relY / (swSz + SWATCH_GAP));
          if (col >= 0 && col < cols) {
            const idx = row * cols + col;
            if (idx >= 0 && idx < colorPalette.length) {
              const layerInfo = TAB_LAYER_MAP[ws.activeTab];
              if (layerInfo) {
                pushHistory(ws);
                patchComposerAppearance({ [layerInfo.colorKey]: idx } as any);
              }
              pushEquipEffect(ws, localX, localY);
              pressControl(ws, `palette_${idx}`);
              playUiSound(state, "ui_click");
              return true;
            }
          }
        }
      }
    }

    return false;
  },

  // ── Drag scroll ──────────────────────────────────────────────────────

  handleDrag(dy, state) {
    const ws = getState(state);
    // Allow overscroll with progressive resistance — the further past the
    // bounds you drag, the less each pixel of drag moves the content. Caps at
    // ~40px of overshoot so the rubberband has a satisfying limit.
    const overshoot = Math.max(
      ws.scrollOffset < 0 ? -ws.scrollOffset : 0,
      ws.scrollOffset > ws.maxScroll ? ws.scrollOffset - ws.maxScroll : 0,
    );
    const resistance = 1 - Math.min(1, overshoot / 40) * 0.6;
    ws.scrollOffset += dy * resistance;
    ws.scrollVelocity = 0; // kill momentum while actively dragging
  },

  handleDragEnd(velocityY, state) {
    const ws = getState(state);
    ws.scrollVelocity = velocityY;
  },

  // ── Lifecycle ─────────────────────────────────────────────────────────

  onOpen(state) {
    const ws = getState(state);
    ws.scrollOffset = 0;
    ws.scrollVelocity = 0;
    ws.previewDir = 0;
    ws.selectedSlot = -1;
    ws.sessionStart = toComposerAppearance();
    ws.history = [];
    ws.openT = 0;
    ws.pressed = null;
    ws.ripple = null;
    ws.flashT = 0;
    ws.prevTab = null;
    ws.tabTransT = 0;
    state.ui.uiSounds.push("ui_open");
  },
};

// ─── Drawing Helpers ────────────────────────────────────────────────────────

/**
 * Draw a compact round action button — used for undo/revert/randomize in the
 * summary banner. Icons are drawn procedurally; no external glyphs.
 */
function drawActionButton(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, size: number,
  icon: "undo" | "revert" | "random" | "save" | "load",
  enabled: boolean, elapsed: number,
  pressT: number = 0,
): void {
  // Press scale — shrinks 12% at full press with slight overshoot on release.
  // iOS buttons compress 10-15%, so this matches that premium feel.
  const scale = 1 - Math.max(0, pressT) * 0.12;
  const cx = x + size / 2;
  const cy = y + size / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);
  ctx.translate(-cx, -cy);
  const r = size / 2 - 1;

  // Background — subtle pill, brighter when enabled, brighter still when pressed
  const pressBoost = pressT * 0.18;
  const alpha = (enabled ? 0.22 : 0.08) + pressBoost;
  ctx.fillStyle = `rgba(255,255,255,${alpha})`;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  // Press ring — a faint pink halo only while pressed
  if (pressT > 0.01) {
    ctx.strokeStyle = `rgba(236,72,153,${pressT * 0.55})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 1, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Icon stroke color
  const strokeAlpha = enabled ? 0.9 : 0.3;
  ctx.strokeStyle = `rgba(255,255,255,${strokeAlpha})`;
  ctx.fillStyle = `rgba(255,255,255,${strokeAlpha})`;
  ctx.lineWidth = 1.2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  switch (icon) {
    case "undo": {
      // Curved arrow pointing left-up
      ctx.beginPath();
      ctx.arc(cx, cy, r * 0.55, Math.PI * 0.2, Math.PI * 1.4);
      ctx.stroke();
      // Arrow head
      const ax = cx - r * 0.55 * Math.cos(Math.PI * 0.4);
      const ay = cy - r * 0.55 * Math.sin(Math.PI * 0.4);
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(ax - 2, ay - 1);
      ctx.moveTo(ax, ay);
      ctx.lineTo(ax - 1, ay + 2);
      ctx.stroke();
      break;
    }
    case "revert": {
      // Double left-chevron — full session reset
      for (let i = 0; i < 2; i++) {
        const ox = cx + (i * 2 - 2);
        ctx.beginPath();
        ctx.moveTo(ox + 1.5, cy - 2.5);
        ctx.lineTo(ox - 1.5, cy);
        ctx.lineTo(ox + 1.5, cy + 2.5);
        ctx.stroke();
      }
      break;
    }
    case "random": {
      // Die face with 5 pips — subtly rotates with elapsed time for "roll" feel
      const rot = enabled ? Math.sin(elapsed * 0.7) * 0.08 : 0;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      const d = r * 0.9;
      ctx.strokeRect(-d / 2, -d / 2, d, d);
      // Pips
      const pipR = 0.7;
      const pipPos = [[-d / 3, -d / 3], [d / 3, -d / 3], [0, 0], [-d / 3, d / 3], [d / 3, d / 3]];
      for (const [px, py] of pipPos) {
        ctx.beginPath();
        ctx.arc(px, py, pipR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      break;
    }
    case "save": {
      // Plus sign for saving to a preset slot
      ctx.beginPath();
      ctx.moveTo(cx - 3, cy); ctx.lineTo(cx + 3, cy);
      ctx.moveTo(cx, cy - 3); ctx.lineTo(cx, cy + 3);
      ctx.stroke();
      break;
    }
    case "load": {
      // Arrow down into tray
      ctx.beginPath();
      ctx.moveTo(cx, cy - 3); ctx.lineTo(cx, cy + 2);
      ctx.moveTo(cx - 2, cy); ctx.lineTo(cx, cy + 2); ctx.lineTo(cx + 2, cy);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - 3, cy + 3); ctx.lineTo(cx + 3, cy + 3);
      ctx.stroke();
      break;
    }
  }
  ctx.restore();
}

function drawOutfitSummary(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  appearance: CharacterAppearance,
  elapsed: number,
  ws: WardrobeState,
): void {
  // Background
  ctx.fillStyle = "rgba(20,16,24,0.7)";
  ctx.fillRect(x, y, w, h);

  // Subtle accent line at top
  const accentGrad = ctx.createLinearGradient(x, y, x + w, y);
  accentGrad.addColorStop(0, "rgba(236,72,153,0)");
  accentGrad.addColorStop(0.5, "rgba(236,72,153,0.5)");
  accentGrad.addColorStop(1, "rgba(244,63,94,0)");
  ctx.fillStyle = accentGrad;
  ctx.fillRect(x, y, w, 1.5);

  // Title
  drawText(ctx, "Current Look", x + 8, y + 4, w - 60, "bold 9px sans-serif", C_TEXT_SECONDARY, "left");

  // ── Action buttons row (top-right) — Undo / Revert / Randomize ───
  // Three compact round buttons.
  const actionY = y + 3;
  const actionSize = 14;
  const actionGap = 3;
  // Positioned right-to-left from the right edge
  const randomizeX = x + w - 4 - actionSize;
  const revertX    = randomizeX - actionGap - actionSize;
  const undoX      = revertX - actionGap - actionSize;

  // Undo button
  const undoEnabled = ws.history.length > 0;
  const undoPress = ws.pressed?.key === "btn_undo" ? ws.pressed.t / 0.14 : 0;
  drawActionButton(ctx, undoX, actionY, actionSize, "undo", undoEnabled, elapsed, undoPress);
  // Revert to session start
  const revertEnabled = ws.sessionStart !== null;
  const revertPress = ws.pressed?.key === "btn_revert" ? ws.pressed.t / 0.14 : 0;
  drawActionButton(ctx, revertX, actionY, actionSize, "revert", revertEnabled, elapsed, revertPress);
  // Randomize button — always active, has a subtle dice-roll glow
  const randomPress = ws.pressed?.key === "btn_random" ? ws.pressed.t / 0.14 : 0;
  drawActionButton(ctx, randomizeX, actionY, actionSize, "random", true, elapsed, randomPress);

  // Summary chips — show currently equipped items with their REAL colors.
  // Pulls live color values from the appearance blob rather than hashing
  // item-name strings (which would produce lying random pastels).
  const chips: { label: string; color: string }[] = [
    { label: formatName(appearance.hairStyle),    color: appearance.hairColour || "#888" },
    { label: formatName(appearance.outfitTop),    color: appearance.outfitTopColour || "#888" },
    { label: formatName(appearance.outfitBottom), color: appearance.outfitBottomColour || "#888" },
    { label: formatName(appearance.shoes),        color: appearance.shoesColour || "#888" },
  ];
  if (appearance.accessory) {
    chips.push({
      label: formatName(appearance.accessory),
      color: appearance.accessoryColour || "#888",
    });
  }

  // Draw chips in a horizontal flow
  let chipX = x + 8;
  const chipY = y + 17;
  const chipH = 14;
  const chipPad = 6;
  const chipGap = 4;

  ctx.font = "8px sans-serif";
  for (const chip of chips) {
    const textW = ctx.measureText(chip.label).width;
    const chipW = textW + chipPad * 2;

    // Don't overflow
    if (chipX + chipW > x + w - 6) break;

    // Chip background uses the ACTUAL equipped color at 20% alpha
    drawRoundRect(ctx, chipX, chipY, chipW, chipH, chipH / 2, adjustAlpha(chip.color, 0.22));

    // Tiny color dot at the chip's leading edge — reinforces the chip/color link
    ctx.fillStyle = chip.color;
    ctx.beginPath();
    ctx.arc(chipX + 4, chipY + chipH / 2, 2, 0, Math.PI * 2);
    ctx.fill();

    // Chip text
    ctx.font = "8px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(chip.label, Math.round(chipX + chipPad + 3), Math.round(chipY + 2.5));

    chipX += chipW + chipGap + 3; // extra gap for the dot
  }

  // Separator
  ctx.fillStyle = C_SEPARATOR;
  ctx.fillRect(x, y + h - 1, w, 1);
}

function drawTabBar(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  ws: WardrobeState,
): void {
  // Background
  ctx.fillStyle = C_TAB_BG;
  ctx.fillRect(x, y, w, h);

  const perTab = w / TABS.length;

  for (let i = 0; i < TABS.length; i++) {
    const tab = TABS[i];
    const tx = x + i * perTab;
    const isActive = tab.id === ws.activeTab;

    // Active indicator — pill background
    if (isActive) {
      const pillPad = 4;
      const pillH = h - 8;
      const pillW = perTab - pillPad * 2;
      const pillX = tx + pillPad;
      const pillY = y + 4;
      const grad = ctx.createLinearGradient(pillX, pillY, pillX, pillY + pillH);
      grad.addColorStop(0, "rgba(236,72,153,0.25)");
      grad.addColorStop(1, "rgba(244,63,94,0.15)");
      drawRoundRect(ctx, pillX, pillY, pillW, pillH, pillH / 2, undefined);
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Label — scale font to fit tab count
    const labelColor = isActive ? C_TAB_ACTIVE : C_TAB_INACTIVE;
    const fontSize = TABS.length > 7 ? 8 : 9;
    const font = isActive ? `bold ${fontSize}px sans-serif` : `${fontSize}px sans-serif`;
    drawText(
      ctx, tab.label,
      tx + perTab / 2, y + 8,
      perTab - 4, font, labelColor, "center",
    );
  }

  // Bottom separator
  ctx.fillStyle = C_SEPARATOR;
  ctx.fillRect(x, y + h - 1, w, 1);
}

function drawItemCell(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number,
  itemKey: string, tab: TabId,
  isEquipped: boolean,
  currentColour: string | undefined,
  elapsed: number,
): void {
  const name = formatName(itemKey);

  // ── Equipped glow ──────────────────────────────────────────────────────
  if (isEquipped) {
    const glowPulse = 0.7 + Math.sin(elapsed * 2.5) * 0.3;

    // Outer glow
    ctx.shadowColor = C_ACCENT;
    ctx.shadowBlur = 8 * glowPulse;
    drawRoundRect(ctx, cx - 1, cy - 1, size + 2, size + 2, CELL_RADIUS + 1, undefined, C_EQUIPPED_RING);
    ctx.strokeStyle = C_EQUIPPED_RING;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
  }

  // ── Cell background — gradient from item hash color ────────────────────
  const baseColor = hashColor(itemKey);
  const lightColor = hashColorLight(itemKey);
  const bgGrad = ctx.createLinearGradient(cx, cy, cx, cy + size);
  bgGrad.addColorStop(0, adjustAlpha(lightColor, 0.25));
  bgGrad.addColorStop(1, adjustAlpha(baseColor, 0.15));
  drawRoundRect(ctx, cx, cy, size, size, CELL_RADIUS, undefined);
  ctx.fillStyle = bgGrad;
  ctx.fill();

  // Subtle border
  ctx.strokeStyle = isEquipped
    ? adjustAlpha(C_EQUIPPED_RING, 0.6)
    : "rgba(255,255,255,0.08)";
  ctx.lineWidth = isEquipped ? 1.5 : 0.5;
  ctx.stroke();

  // ── Item visual — actual character layer preview, or icon fallback ──────
  const iconCx = cx + size / 2;
  const iconCy = cy + size / 2 - 2;
  const tabDef = TABS.find(t => t.id === tab);
  const itemIdx = tabDef ? tabDef.options.indexOf(itemKey) : -1;
  const drewPreview = itemIdx >= 0 && drawLayerPreview(ctx, iconCx, iconCy, size, itemIdx, tab);
  if (!drewPreview) {
    const iconTint = isEquipped && currentColour ? currentColour : hashColor(itemKey);
    drawItemIcon(ctx, iconCx, iconCy, size, itemKey, tab, iconTint);
  }

  // ── Color preview swatch (if the item has an associated colour) ────────
  if (currentColour && isEquipped) {
    const swatchSize = 8;
    const swatchX = cx + size - swatchSize - 4;
    const swatchY = cy + 4;
    drawRoundRect(ctx, swatchX, swatchY, swatchSize, swatchSize, 2, currentColour, "rgba(255,255,255,0.3)");
  }

  // ── Equipped badge ─────────────────────────────────────────────────────
  if (isEquipped) {
    const badgeR = 5;
    const badgeX = cx + size - badgeR - 2;
    const badgeY = cy + size - badgeR - 2;
    ctx.beginPath();
    ctx.arc(badgeX, badgeY, badgeR, 0, Math.PI * 2);
    ctx.fillStyle = C_ACCENT;
    ctx.fill();

    // Checkmark
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(badgeX - 2.5, badgeY);
    ctx.lineTo(badgeX - 0.5, badgeY + 2);
    ctx.lineTo(badgeX + 2.5, badgeY - 2);
    ctx.stroke();
  }

  // ── Label below cell ───────────────────────────────────────────────────
  const labelY = cy + size + 2;
  const labelColor = isEquipped ? C_TEXT_PRIMARY : C_TEXT_DIM;
  const labelFont = isEquipped ? "bold 7px sans-serif" : "7px sans-serif";
  drawText(ctx, name, cx + size / 2, labelY, size, labelFont, labelColor, "center");
}

// ─── Utility ────────────────────────────────────────────────────────────────

function adjustAlpha(color: string, alpha: number): string {
  // Handle hex colors
  if (color.startsWith("#")) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  // Handle hsl colors — convert to rgba approximation
  const hslMatch = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (hslMatch) {
    const [, h, s, l] = hslMatch.map(Number);
    const rgb = hslToRgb(h / 360, s / 100, l / 100);
    return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
  }
  return color;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

// ─── Body Panel — Paper Doll + RPG Equipment Slots ─────────────────────────

interface SlotDef {
  label: string;
  composerKey: keyof ComposerAppearance;
  icon: string;
  /** X offset from center (negative = left of character) */
  ox: number;
  /** Y offset from top of panel */
  oy: number;
}

/** Paper-doll slots — positioned around character preview (oy 0-2 only) */
const OUTFIT_SLOTS: SlotDef[] = [
  { label: "Hat",      composerKey: "hatStyle",      icon: "🎩", ox: -1,  oy: 0 },
  { label: "Top",      composerKey: "topStyle",      icon: "👕", ox: -1,  oy: 1 },
  { label: "Jacket",   composerKey: "jacketStyle",   icon: "🧥", ox: 1,   oy: 1 },
  { label: "Bottom",   composerKey: "bottomStyle",   icon: "👖", ox: -1,  oy: 2 },
  { label: "Shoes",    composerKey: "shoeStyle",     icon: "👟", ox: 1,   oy: 2 },
  { label: "Held",     composerKey: "heldItem",      icon: "✋", ox: 1,   oy: 0 },
];

/** Extras — inline scrollable controls (don't have body positions) */
interface ExtraDef {
  label: string;
  icon: string;
  styleKey: keyof ComposerAppearance;
  colorKey: keyof ComposerAppearance | null;
  labels: readonly string[];
  maxStyle: number;
}

const EXTRAS: ExtraDef[] = [
  { label: "Wings",    icon: "🦋", styleKey: "wingsStyle",    colorKey: "wingsColor",    labels: WINGS_LABELS,    maxStyle: 3 },
  { label: "Cape",     icon: "🧣", styleKey: "capeStyle",     colorKey: "capeColor",     labels: CAPE_LABELS,     maxStyle: 2 },
  { label: "Pack",     icon: "🎒", styleKey: "backpackStyle", colorKey: "backpackColor", labels: BACKPACK_LABELS, maxStyle: 2 },
  { label: "Aura",     icon: "✨", styleKey: "auraStyle",     colorKey: null,            labels: AURA_LABELS,     maxStyle: 3 },
];

/** Map style keys to their color counterparts (null = no color) */
const SLOT_COLOR_KEY: Record<string, keyof ComposerAppearance | null> = {
  hatStyle:      "hatColor",
  topStyle:      "topColor",
  jacketStyle:   "jacketColor",
  bottomStyle:   "bottomColor",
  shoeStyle:     "shoeColor",
  heldItem:      null,
};

function drawBodyPanel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  state: GameState,
  ws: WardrobeState,
): void {
  const composerApp = toComposerAppearance();
  const pad = 6;
  const isNude = composerApp.nude;

  // ── Character Preview — center of panel ──
  const previewH = Math.min(h * 0.45, 100);
  const previewCx = x + w / 2;
  const previewCy = y + pad + previewH * 0.45;

  // Dark backdrop for character
  const bgGrad = ctx.createRadialGradient(previewCx, previewCy, 0, previewCx, previewCy, previewH * 0.7);
  bgGrad.addColorStop(0, "rgba(40,30,50,0.4)");
  bgGrad.addColorStop(1, "rgba(20,16,24,0)");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(x, y, w, previewH + pad * 2);

  // Render character preview — alive, animated, rotatable.
  // 6 FPS 8-frame cycle gives a clear breathing rhythm (1.33s full cycle).
  // The composer internally maps frame 0-7 across body sway + blink timing.
  const pCtx = getPreviewCtx();
  const previewDirStr = PREVIEW_DIRS[ws.previewDir] ?? "s";
  const previewFrame = Math.floor(ws.elapsed * 6) % 8;
  if (pCtx) {
    pCtx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    pCtx.save();
    pCtx.translate(PREVIEW_SIZE / 2, PREVIEW_SIZE / 2 + 8);
    pCtx.scale(0.65, 0.65);
    renderLayerPreview(pCtx, composerApp, ["shadow", "aura", "cape", "wings", "backpack", "body", "anatomy", "skin-detail", "bottom", "shoes", "top", "jacket", "eyes", "facial-hair", "makeup", "lips", "hair-front", "hair-back", "hat", "accessory"], previewDirStr, previewFrame);
    pCtx.restore();
    const drawScale = previewH / PREVIEW_SIZE * 1.4;
    ctx.save();
    ctx.translate(previewCx, previewCy);
    ctx.scale(drawScale, drawScale);
    ctx.drawImage(_previewCanvas!, -PREVIEW_SIZE / 2, -PREVIEW_SIZE / 2);
    ctx.restore();
  }

  // Rotation affordance — subtle arrows at preview edges
  {
    const arrowY = y + previewH + pad - 2;
    ctx.font = "8px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.textAlign = "left";
    ctx.fillText("◀", x + pad, arrowY);
    ctx.textAlign = "right";
    ctx.fillText("▶", x + w - pad, arrowY);
    // Direction label
    ctx.textAlign = "center";
    ctx.font = "6px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    const dirNames = ["S", "SW", "W", "NW", "N", "NE", "E", "SE"];
    ctx.fillText(dirNames[ws.previewDir], previewCx, arrowY);
  }

  // ── Outfit Slots — arranged around character ──
  const slotSize = 28;
  const slotPad = 4;
  const slotColW = slotSize + slotPad * 2;
  const slotRowH = 26;
  const leftCol = x + pad;
  const rightCol = x + w - pad - slotColW;
  const slotsTopY = y + pad + 6;

  for (let si = 0; si < OUTFIT_SLOTS.length; si++) {
    const slot = OUTFIT_SLOTS[si];
    const sx = slot.ox < 0 ? leftCol : rightCol;
    const sy = slotsTopY + slot.oy * (slotRowH + 4);
    const val = composerApp[slot.composerKey] as number;
    const isEmpty = val < 0;
    const isStripped = isNude;
    const isSelected = ws.selectedSlot === si;

    // Slot background
    if (isSelected) {
      ctx.fillStyle = "rgba(236,72,153,0.25)";
    } else if (isStripped && slot.composerKey !== "hatStyle" && slot.composerKey !== "heldItem") {
      ctx.fillStyle = "rgba(80,30,40,0.3)";
    } else if (isEmpty) {
      ctx.fillStyle = "rgba(44,44,46,0.4)";
    } else {
      ctx.fillStyle = C_BG_CARD;
    }
    drawRoundRect(ctx, sx, sy, slotColW, slotRowH, 6);
    ctx.fill();

    // Selection border
    if (isSelected) {
      ctx.strokeStyle = C_ACCENT;
      ctx.lineWidth = 1.5;
      drawRoundRect(ctx, sx, sy, slotColW, slotRowH, 6);
      ctx.stroke();
    }

    // Dashed border for empty
    if (isEmpty && !isStripped && !isSelected) {
      ctx.setLineDash([2, 2]);
      ctx.strokeStyle = C_TEXT_DIM;
      ctx.lineWidth = 0.8;
      drawRoundRect(ctx, sx + 1, sy + 1, slotColW - 2, slotRowH - 2, 5);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Color dot indicator (small swatch in corner if slot has color)
    const colorKey = SLOT_COLOR_KEY[slot.composerKey as string];
    if (colorKey && !isEmpty) {
      const colorIdx = (composerApp[colorKey] as number) ?? 0;
      const swatch = ITEM_COLORS[colorIdx] ?? "#888";
      ctx.fillStyle = swatch;
      ctx.beginPath();
      ctx.arc(sx + slotColW - 4, sy + 4, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.3)";
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // Icon + label
    ctx.font = "11px sans-serif";
    ctx.textAlign = "center";
    ctx.fillStyle = isStripped ? "rgba(255,100,100,0.5)" : isEmpty ? C_TEXT_DIM : C_TEXT_PRIMARY;
    ctx.fillText(slot.icon, sx + slotColW / 2, sy + 13);
    ctx.font = "7px sans-serif";
    ctx.fillStyle = isSelected ? C_ACCENT : C_TEXT_DIM;
    ctx.fillText(slot.label, sx + slotColW / 2, sy + 22);
  }

  // ── Controls Section — scrollable below character ──
  const controlsTop = y + previewH + pad * 2 + 4;
  const controlsH = y + h - controlsTop;
  const btnW = w - pad * 2;
  const btnH = 28;
  const btnX = x + pad;

  // Measure total controls height (dry run) to set maxScroll
  const totalControlsH = measureBodyControlsH(composerApp, isNude, btnW, ws.selectedSlot);
  ws.maxScroll = Math.max(0, totalControlsH - controlsH);
  ws.scrollOffset = Math.max(0, Math.min(ws.scrollOffset, ws.maxScroll));

  // Clip and scroll the controls area
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, controlsTop, w, controlsH);
  ctx.clip();

  let cy = controlsTop - ws.scrollOffset;

  // ── Selected Slot: Style Chip Row + Color Palette ──
  // When the user taps a paper-doll slot, drop a focused editor for it right
  // at the top of the scroll area — section header, style chip row, color.
  // This is the only place Hat / Jacket / Held can be edited with a visual
  // picker; top-level tabs don't exist for those three slots.
  const selectedSlot = ws.selectedSlot >= 0 ? OUTFIT_SLOTS[ws.selectedSlot] : null;
  const selectedColorKey = selectedSlot ? SLOT_COLOR_KEY[selectedSlot.composerKey as string] : null;
  if (selectedSlot) {
    cy = drawSectionHeader(ctx, `${selectedSlot.label}`, btnX, btnW, cy);
    const styleLabels = SLOT_STYLE_LABELS[selectedSlot.composerKey as string];
    if (styleLabels) {
      const styleVal = (composerApp[selectedSlot.composerKey] as number) ?? -1;
      // -1 → chip index 0 (the "None" chip).
      cy = drawChipRow(ctx, "Style", styleLabels, styleVal + 1, btnX, btnW, cy);
      cy += 2;
    }
    if (selectedColorKey) {
      const palette = selectedSlot.composerKey === "hairStyle" ? HAIR_COLORS : ITEM_COLORS;
      cy = drawPaletteRow(ctx, "Color", palette, (composerApp[selectedColorKey] as number) ?? 0, btnX, btnW, cy);
      cy += 2;
    }
  }

  // ── Outfit Presets — 6 quick-save slots ──
  {
    cy = drawSectionHeader(ctx, "Presets", btnX, btnW, cy);
    cy = drawPresetStrip(ctx, btnX, btnW, cy, ws);
    cy += 4;
  }

  // ── Extras — Wings / Cape / Backpack / Aura ──
  {
    cy = drawSectionHeader(ctx, "Extras", btnX, btnW, cy);

    for (const extra of EXTRAS) {
      const styleVal = (composerApp[extra.styleKey] as number) ?? -1;
      cy = drawChipRow(ctx, extra.label, extra.labels, styleVal + 1, btnX, btnW, cy);
      cy += 2;
      // Color palette — only when style is active and has color
      if (extra.colorKey && styleVal >= 0) {
        cy = drawPaletteRow(ctx, `${extra.label} Clr`, ITEM_COLORS, (composerApp[extra.colorKey] as number) ?? 0, btnX, btnW, cy);
        cy += 2;
      }
    }
  }

  // ── Skin Tone ──
  cy = drawPaletteRow(ctx, "Skin Tone", SKIN_TONES, (composerApp as any).skinTone ?? 3, btnX, btnW, cy);
  cy += 1;

  // ── Skin Detail ──
  cy = drawChipRow(ctx, "Skin", SKIN_DETAIL_LABELS, ((composerApp as any).skinDetail ?? -1) + 1, btnX, btnW, cy);
  cy += 2;

  // ── Build (bodyType) ──
  cy = drawChipRow(ctx, "Build", BODY_LABELS, (composerApp as any).bodyType ?? 1, btnX, btnW, cy);
  cy += 2;

  // ── Build Archetype — Primary & Secondary ──
  {
    const arch1 = ARCHETYPE_IDS.indexOf((composerApp as any).bodyArchetype1 ?? "lithe");
    const arch2 = ARCHETYPE_IDS.indexOf((composerApp as any).bodyArchetype2 ?? "lithe");
    cy = drawChipRow(ctx, "Type 1", ARCHETYPE_LABELS, arch1 >= 0 ? arch1 : 2, btnX, btnW, cy);
    cy += 2;
    cy = drawChipRow(ctx, "Type 2", ARCHETYPE_LABELS, arch2 >= 0 ? arch2 : 2, btnX, btnW, cy);
    cy += 2;
  }

  // ── Blend + Body Scale sliders ──
  {
    const blendSliders: Array<{ label: string; key: string; val: number; max: number; labels: readonly string[] }> = [
      { label: "Blend", key: "archetypeBlend", val: Math.round(((composerApp as any).archetypeBlend ?? 0) * 4), max: 4, labels: BLEND_LABELS },
      { label: "Scale", key: "bodyScale", val: (composerApp as any).bodyScale ?? 2, max: 4, labels: SCALE_LABELS },
    ];
    for (const slider of blendSliders) {
      ctx.font = "8px sans-serif";
      ctx.textAlign = "left";
      ctx.fillStyle = C_TEXT_SECONDARY;
      ctx.fillText(slider.label, btnX + 4, cy + 10);

      const trackX = btnX + 52;
      const trackW = btnW - 60;
      const trackY = cy + 6;
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      drawRoundRect(ctx, trackX, trackY, trackW, 4, 2);
      ctx.fill();

      const fillRatio = slider.val / slider.max;
      ctx.fillStyle = C_ACCENT;
      drawRoundRect(ctx, trackX, trackY, trackW * fillRatio, 4, 2);
      ctx.fill();

      for (let d = 0; d <= slider.max; d++) {
        const dx = trackX + (trackW * d / slider.max);
        ctx.fillStyle = d <= slider.val ? C_ACCENT : "rgba(255,255,255,0.2)";
        ctx.beginPath(); ctx.arc(dx, trackY + 2, 3, 0, Math.PI * 2); ctx.fill();
      }

      // Value label
      ctx.font = "6px sans-serif";
      ctx.textAlign = "right";
      ctx.fillStyle = C_TEXT_DIM;
      ctx.fillText(slider.labels[slider.val] ?? "", btnX + btnW - 2, cy + 10);

      cy += 18;
    }
  }

  // ── Nude Toggle Button ──
  // Big toggle button
  if (isNude) {
    const nudeGrad = ctx.createLinearGradient(btnX, cy, btnX + btnW, cy);
    nudeGrad.addColorStop(0, "rgba(180,60,80,0.35)");
    nudeGrad.addColorStop(1, "rgba(140,40,60,0.25)");
    ctx.fillStyle = nudeGrad;
  } else {
    ctx.fillStyle = C_BG_CARD;
  }
  drawRoundRect(ctx, btnX, cy, btnW, btnH, btnH / 2);
  ctx.fill();
  // Border
  ctx.strokeStyle = isNude ? "rgba(255,100,120,0.4)" : C_SEPARATOR;
  ctx.lineWidth = 1;
  drawRoundRect(ctx, btnX, cy, btnW, btnH, btnH / 2);
  ctx.stroke();
  // Label
  ctx.font = "bold 10px sans-serif";
  ctx.textAlign = "center";
  ctx.fillStyle = isNude ? "#ff9aaa" : C_TEXT_PRIMARY;
  ctx.fillText(isNude ? "🔓  NUDE  🔓" : "👔  CLOTHED", x + w / 2, cy + 17);

  cy += btnH + 8;

  // ── Anatomy selector — always visible ──
  const gender = composerApp.bodyGender ?? "androgynous";
  {
    const genderOpts: Array<{ id: string; label: string }> = [
      { id: "feminine", label: "♀ Feminine" },
      { id: "masculine", label: "♂ Masculine" },
      { id: "androgynous", label: "⚥ Andro" },
    ];
    const optW = Math.floor((btnW - 4) / genderOpts.length);

    for (let i = 0; i < genderOpts.length; i++) {
      const opt = genderOpts[i];
      const ox = btnX + i * (optW + 2);
      const isActive = gender === opt.id;

      ctx.fillStyle = isActive ? "rgba(236,72,153,0.3)" : "rgba(44,44,46,0.5)";
      drawRoundRect(ctx, ox, cy, optW, 22, 6);
      ctx.fill();
      if (isActive) {
        ctx.strokeStyle = C_ACCENT;
        ctx.lineWidth = 1;
        drawRoundRect(ctx, ox, cy, optW, 22, 6);
        ctx.stroke();
      }

      ctx.font = isActive ? "bold 8px sans-serif" : "8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = isActive ? C_ACCENT : C_TEXT_SECONDARY;
      ctx.fillText(opt.label, ox + optW / 2, cy + 14);
    }
    cy += 28;
  }

  // ── Body shape sliders — always visible (body shape affects clothed silhouette) ──
  {
    const sliders: Array<{ label: string; key: string; val: number; max: number }> = [];
    sliders.push({ label: "Shoulders", key: "shoulderAdjust", val: (composerApp as any).shoulderAdjust ?? 2, max: 4 });
    sliders.push({ label: "Waist", key: "waistAdjust", val: (composerApp as any).waistAdjust ?? 2, max: 4 });
    sliders.push({ label: "Hips", key: "hipAdjust", val: (composerApp as any).hipAdjust ?? 2, max: 4 });
    sliders.push({ label: "Leg Gap", key: "legGap", val: (composerApp as any).legGap ?? 2, max: 4 });
    if (gender === "feminine" || gender === "androgynous") {
      sliders.push({ label: "Breasts", key: "breastSize", val: (composerApp as any).breastSize ?? 2, max: 4 });
    }
    sliders.push({ label: "Butt", key: "buttSize", val: (composerApp as any).buttSize ?? 2, max: 4 });
    // Genital size — only when nude + masculine
    if (isNude && gender === "masculine") {
      sliders.push({ label: "Genitals", key: "genitalSize", val: (composerApp as any).genitalSize ?? 2, max: 4 });
    }

    for (const slider of sliders) {
      ctx.font = "8px sans-serif";
      ctx.textAlign = "left";
      ctx.fillStyle = C_TEXT_SECONDARY;
      ctx.fillText(slider.label, btnX + 4, cy + 10);

      const trackX = btnX + 52;
      const trackW = btnW - 60;
      const trackY = cy + 6;
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      drawRoundRect(ctx, trackX, trackY, trackW, 4, 2);
      ctx.fill();

      const fillRatio = slider.val / slider.max;
      ctx.fillStyle = C_ACCENT;
      drawRoundRect(ctx, trackX, trackY, trackW * fillRatio, 4, 2);
      ctx.fill();

      for (let d = 0; d <= slider.max; d++) {
        const dx = trackX + (trackW * d / slider.max);
        ctx.fillStyle = d <= slider.val ? C_ACCENT : "rgba(255,255,255,0.2)";
        ctx.beginPath(); ctx.arc(dx, trackY + 2, 3, 0, Math.PI * 2); ctx.fill();
      }

      cy += 18;
    }
  }

  ctx.restore(); // controls clip

  // Scroll indicator for controls section
  if (ws.maxScroll > 0) {
    const trackH = controlsH - 8;
    const trackX2 = x + w - 3;
    const trackY2 = controlsTop + 4;
    const thumbRatio = controlsH / (totalControlsH || 1);
    const thumbH = Math.max(12, trackH * thumbRatio);
    const scrollRatio = ws.scrollOffset / ws.maxScroll;
    const thumbY2 = trackY2 + scrollRatio * (trackH - thumbH);
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    drawRoundRect(ctx, trackX2, trackY2, 2.5, trackH, 1.25);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    drawRoundRect(ctx, trackX2, thumbY2, 2.5, thumbH, 1.25);
    ctx.fill();
  }

  ctx.textAlign = "left"; // reset
}

/**
 * Measure the total height of body panel controls for scroll calculation.
 * Must match the draw code exactly.
 */
function measureBodyControlsH(composerApp: ComposerAppearance, isNude: boolean, btnW: number, selectedSlot: number): number {
  const gender = composerApp.bodyGender ?? "androgynous";
  let h = 0;
  // Selected slot style chip row + color palette (conditional)
  if (selectedSlot >= 0 && selectedSlot < OUTFIT_SLOTS.length) {
    const slot = OUTFIT_SLOTS[selectedSlot];
    // Section header for the slot
    h += 16;
    // Style chip row
    const styleLabels = SLOT_STYLE_LABELS[slot.composerKey as string];
    if (styleLabels) h += chipRowH(styleLabels.length) + 2;
    const colorKey = SLOT_COLOR_KEY[slot.composerKey as string];
    if (colorKey) {
      const palette = slot.composerKey === "hairStyle" ? HAIR_COLORS : ITEM_COLORS;
      h += paletteRowHeight(palette.length, btnW) + 2;
    }
  }
  // Presets section header + strip
  h += 16 + PRESET_H + 2 + 4;
  // Extras section header + chip rows + conditional palettes
  h += 16;
  for (const extra of EXTRAS) {
    h += chipRowH(extra.labels.length);
    const styleVal = (composerApp[extra.styleKey] as number) ?? -1;
    if (extra.colorKey && styleVal >= 0) {
      h += paletteRowHeight(ITEM_COLORS.length, btnW) + 2;
    }
  }
  // Skin Tone palette
  h += paletteRowHeight(SKIN_TONES.length, btnW) + 1;
  // Skin Detail chips
  h += chipRowH(SKIN_DETAIL_LABELS.length);
  // Build chips
  h += chipRowH(BODY_LABELS.length);
  // Type 1 chips
  h += chipRowH(ARCHETYPE_LABELS.length);
  // Type 2 chips
  h += chipRowH(ARCHETYPE_LABELS.length);
  // Blend slider
  h += 18;
  // Scale slider
  h += 18;
  // Nude toggle
  h += 28 + 8;
  // Anatomy selector
  h += 28;
  // Shoulder, Waist, Hips, LegGap sliders
  h += 18 * 4;
  // Breast slider (conditional)
  if (gender === "feminine" || gender === "androgynous") h += 18;
  // Butt slider
  h += 18;
  // Genital slider (conditional)
  if (isNude && gender === "masculine") h += 18;
  return h + 8; // bottom padding
}

// ─── Face Panel ────────────────────────────────────────────────────────────

/**
 * Reusable: draw a row of chip buttons and return cy advancement.
 * Returns the Y value AFTER this row.
 */
/** Total Y advance for a chip row (including gap after). Matches drawChipRow. */
function chipRowH(count: number): number {
  // Single row: chipH(20) + gap(4) = 24. Multi-row: 2*20 + 3 + 4 = 47.
  return count > 5 ? 47 : 24;
}

/**
 * Draw a section header: bold uppercase label with an accent underline bar.
 * Gives the panels real visual hierarchy beyond the flat chip-row labels.
 * Returns the Y position after the header (caller continues drawing below).
 */
function drawSectionHeader(
  ctx: CanvasRenderingContext2D,
  label: string, btnX: number, btnW: number, cy: number,
): number {
  // Tiny divider above the header for breathing room
  ctx.fillStyle = C_SEPARATOR;
  ctx.fillRect(btnX, cy + 1, btnW, 1);
  // Header label — bold, uppercase, letterspaced
  ctx.font = "bold 7px sans-serif";
  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  const spaced = label.toUpperCase().split("").join(" ");
  ctx.fillText(spaced, Math.round(btnX + 2), Math.round(cy + 12));
  // Accent underline bar — fades from pink to transparent
  const textW = ctx.measureText(spaced).width;
  const barGrad = ctx.createLinearGradient(btnX + 2, 0, btnX + 2 + textW + 20, 0);
  barGrad.addColorStop(0, "rgba(236,72,153,0.75)");
  barGrad.addColorStop(1, "rgba(236,72,153,0)");
  ctx.fillStyle = barGrad;
  ctx.fillRect(btnX + 2, cy + 15, textW + 20, 1);
  return cy + 16;
}

/** Draw chip content — either a label or a slashed-circle "None" glyph. */
function drawChipContent(
  ctx: CanvasRenderingContext2D,
  label: string, cx2: number, cy2: number, chipW: number,
  active: boolean,
): void {
  if (label === "None") {
    // Slashed circle glyph — universal visual grammar for "empty"
    const g = cx2 + chipW / 2;
    ctx.save();
    ctx.strokeStyle = active ? C_ACCENT : "rgba(255,255,255,0.5)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(g, cy2, 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(g - 2.2, cy2 + 2.2);
    ctx.lineTo(g + 2.2, cy2 - 2.2);
    ctx.stroke();
    ctx.restore();
    return;
  }
  ctx.font = "6px sans-serif";
  ctx.textAlign = "center";
  ctx.fillStyle = active ? C_ACCENT : "rgba(255,255,255,0.55)";
  ctx.fillText(label, Math.round(g2(cx2, chipW)), Math.round(cy2 + 1));
}
function g2(cx2: number, chipW: number): number { return cx2 + chipW / 2; }

function drawChipRow(
  ctx: CanvasRenderingContext2D,
  label: string, labels: readonly string[],
  activeIdx: number, btnX: number, btnW: number, cy: number,
): number {
  ctx.font = "8px sans-serif";
  ctx.textAlign = "left";
  ctx.fillStyle = C_TEXT_SECONDARY;
  ctx.fillText(label, Math.round(btnX + 2), Math.round(cy + 9));

  const count = labels.length;
  const chipX = btnX + 52;
  const chipAreaW = btnW - 56;

  // Chip dimensions — 20px tall for better touch targets (was 12px).
  // Still smaller than iOS 44px minimum but reasonable for dense picker UIs.
  const chipH = 20;
  const chipRadius = 6;

  if (count <= 5) {
    // Single row
    const chipW = Math.floor(chipAreaW / count) - 2;
    for (let i = 0; i < count; i++) {
      const cx2 = chipX + i * (chipW + 2);
      const active = i === activeIdx;
      ctx.fillStyle = active ? "rgba(236,72,153,0.3)" : "rgba(44,44,46,0.5)";
      drawRoundRect(ctx, cx2, cy, chipW, chipH, chipRadius);
      ctx.fill();
      if (active) {
        ctx.strokeStyle = C_ACCENT;
        ctx.lineWidth = 1;
        drawRoundRect(ctx, cx2, cy, chipW, chipH, chipRadius);
        ctx.stroke();
      }
      drawChipContent(ctx, labels[i], cx2, cy + chipH / 2 - 1, chipW, active);
    }
    return cy + chipH + 4;
  }

  // Multi-row: 2 rows for 6+ items — keeps chips tappable on phone
  const perRow = Math.ceil(count / 2);
  const chipW = Math.floor(chipAreaW / perRow) - 2;
  const rowGap = 3;
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const cx2 = chipX + col * (chipW + 2);
    const ry = cy + row * (chipH + rowGap);
    const active = i === activeIdx;
    ctx.fillStyle = active ? "rgba(236,72,153,0.3)" : "rgba(44,44,46,0.5)";
    drawRoundRect(ctx, cx2, ry, chipW, chipH, chipRadius);
    ctx.fill();
    if (active) {
      ctx.strokeStyle = C_ACCENT;
      ctx.lineWidth = 1;
      drawRoundRect(ctx, cx2, ry, chipW, chipH, chipRadius);
      ctx.stroke();
    }
    drawChipContent(ctx, labels[i], cx2, ry + chipH / 2 - 1, chipW, active);
  }
  return cy + 2 * chipH + rowGap + 4;
}

/**
 * Reusable: draw a color palette swatch grid. Wraps to multiple rows when
 * swatches would be smaller than MIN_SWATCH on a single row.
 * Returns the Y position after the last row.
 */
// Swatch sizes — larger than typical for better touch targets.
// 44px iOS minimum is impractical in dense UIs, but 16-20px is a
// significant improvement over 8-10px while keeping the palette usable.
const MIN_SWATCH = 16;
const MAX_SWATCH = 20;
const SWATCH_GAP = 2;

function drawPaletteRow(
  ctx: CanvasRenderingContext2D,
  label: string, palette: readonly string[],
  activeIdx: number, btnX: number, btnW: number, cy: number,
): number {
  ctx.font = "8px sans-serif";
  ctx.textAlign = "left";
  ctx.fillStyle = C_TEXT_SECONDARY;
  ctx.fillText(label, btnX + 2, cy + 9);

  const paletteX = btnX + 52;
  const availW = btnW - 56;
  // Calculate how many fit per row at minimum size
  const perRowAtMin = Math.floor(availW / (MIN_SWATCH + SWATCH_GAP));
  const cols = Math.min(palette.length, perRowAtMin);
  const swatchSz = Math.min(MAX_SWATCH, Math.floor(availW / cols) - SWATCH_GAP);
  const rowCount = Math.ceil(palette.length / cols);

  for (let i = 0; i < palette.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const sx = paletteX + col * (swatchSz + SWATCH_GAP);
    const sy = cy + 1 + row * (swatchSz + SWATCH_GAP);
    ctx.fillStyle = palette[i];
    drawRoundRect(ctx, sx, sy, swatchSz, swatchSz, 2);
    ctx.fill();
    // Glint — top-edge highlight that lifts the swatch off the background
    const glintGrad = ctx.createLinearGradient(sx, sy, sx, sy + swatchSz * 0.4);
    glintGrad.addColorStop(0, "rgba(255,255,255,0.35)");
    glintGrad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = glintGrad;
    drawRoundRect(ctx, sx, sy, swatchSz, swatchSz * 0.4, 2);
    ctx.fill();
    if (i === activeIdx) {
      ctx.strokeStyle = C_ACCENT;
      ctx.lineWidth = 1.5;
      drawRoundRect(ctx, sx - 1, sy - 1, swatchSz + 2, swatchSz + 2, 3);
      ctx.stroke();
      // Inner highlight dot — marks the selection with a bright center
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.beginPath();
      ctx.arc(sx + swatchSz / 2, sy + swatchSz / 2, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  return cy + rowCount * (swatchSz + SWATCH_GAP) + 4;
}

/**
 * Reusable: handle tap on chip row. Returns selected index or -1.
 */
function hitChipRow(
  localX: number, localY: number,
  count: number, btnX: number, btnW: number, cy: number,
): number {
  const chipX = btnX + 52;
  const chipAreaW = btnW - 56;
  const chipH = 20;
  const rowGap = 3;

  if (count <= 5) {
    // Single row
    if (localY < cy || localY >= cy + chipH) return -1;
    const chipW = Math.floor(chipAreaW / count) - 2;
    for (let i = 0; i < count; i++) {
      const cx2 = chipX + i * (chipW + 2);
      if (localX >= cx2 && localX < cx2 + chipW) return i;
    }
    return -1;
  }

  // Multi-row: 2 rows
  const totalH = 2 * chipH + rowGap;
  if (localY < cy || localY >= cy + totalH) return -1;
  const perRow = Math.ceil(count / 2);
  const chipW = Math.floor(chipAreaW / perRow) - 2;
  const row = localY < cy + chipH + rowGap / 2 ? 0 : 1;
  const ry = cy + row * (chipH + rowGap);
  if (localY < ry || localY >= ry + chipH) return -1; // within chip bounds
  for (let col = 0; col < perRow; col++) {
    const idx = row * perRow + col;
    if (idx >= count) break;
    const cx2 = chipX + col * (chipW + 2);
    if (localX >= cx2 && localX < cx2 + chipW) return idx;
  }
  return -1;
}

/**
 * Reusable: handle tap on palette grid. Returns selected index or -1.
 */
function hitPaletteRow(
  localX: number, localY: number,
  paletteLen: number, btnX: number, btnW: number, cy: number,
): number {
  const paletteX = btnX + 52;
  const availW = btnW - 56;
  const perRowAtMin = Math.floor(availW / (MIN_SWATCH + SWATCH_GAP));
  const cols = Math.min(paletteLen, perRowAtMin);
  const swatchSz = Math.min(MAX_SWATCH, Math.floor(availW / cols) - SWATCH_GAP);
  const rowCount = Math.ceil(paletteLen / cols);
  const totalH = rowCount * (swatchSz + SWATCH_GAP) + 3;
  if (localY < cy || localY >= cy + totalH) return -1;
  const relY = localY - cy - 1;
  const relX = localX - paletteX;
  if (relX < 0) return -1;
  const row = Math.floor(relY / (swatchSz + SWATCH_GAP));
  const col = Math.floor(relX / (swatchSz + SWATCH_GAP));
  if (col >= cols || row >= rowCount) return -1;
  const idx = row * cols + col;
  return idx < paletteLen ? idx : -1;
}

// ─── Preset Strip ──────────────────────────────────────────────────────────
// A row of 6 quick-save outfit slots. Empty slots show a dashed "+" and
// save the current look when tapped. Filled slots show the slot index
// plus a tiny tint pulled from the saved top-color and load on tap. A
// small × in the top-right clears a filled slot.

const PRESET_COUNT = 6;
const PRESET_H = 22;

function drawPresetStrip(
  ctx: CanvasRenderingContext2D,
  btnX: number, btnW: number, cy: number, ws: WardrobeState,
): number {
  const slotGap = 3;
  const slotW = Math.floor((btnW - slotGap * (PRESET_COUNT - 1)) / PRESET_COUNT);
  for (let i = 0; i < PRESET_COUNT; i++) {
    const sx = btnX + i * (slotW + slotGap);
    const sy = cy;
    const saved = ws.savedOutfits[i] ?? null;

    if (saved) {
      // Filled — use the saved top-color as a soft fill to identify outfits
      const topIdx = saved.topColor ?? 0;
      const tintHex = ITEM_COLORS[topIdx] ?? "#888";
      const grad = ctx.createLinearGradient(sx, sy, sx, sy + PRESET_H);
      grad.addColorStop(0, adjustAlpha(tintHex, 0.55));
      grad.addColorStop(1, adjustAlpha(tintHex, 0.2));
      ctx.fillStyle = grad;
      drawRoundRect(ctx, sx, sy, slotW, PRESET_H, 4);
      ctx.fill();
      ctx.strokeStyle = adjustAlpha(tintHex, 0.8);
      ctx.lineWidth = 1;
      drawRoundRect(ctx, sx, sy, slotW, PRESET_H, 4);
      ctx.stroke();

      // Slot index — bold center
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.fillText(String(i + 1), Math.round(sx + slotW / 2), Math.round(sy + PRESET_H / 2));

      // Clear × in top-right corner
      const xSize = 4;
      const xCx = sx + slotW - 5;
      const xCy = sy + 4;
      ctx.strokeStyle = "rgba(255,255,255,0.55)";
      ctx.lineWidth = 0.8;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(xCx - xSize / 2, xCy - xSize / 2);
      ctx.lineTo(xCx + xSize / 2, xCy + xSize / 2);
      ctx.moveTo(xCx + xSize / 2, xCy - xSize / 2);
      ctx.lineTo(xCx - xSize / 2, xCy + xSize / 2);
      ctx.stroke();
    } else {
      // Empty — dashed border with a + glyph, soft dark fill
      ctx.fillStyle = "rgba(40,30,50,0.3)";
      drawRoundRect(ctx, sx, sy, slotW, PRESET_H, 4);
      ctx.fill();
      ctx.setLineDash([2, 2]);
      ctx.strokeStyle = "rgba(255,255,255,0.25)";
      ctx.lineWidth = 0.8;
      drawRoundRect(ctx, sx + 0.5, sy + 0.5, slotW - 1, PRESET_H - 1, 4);
      ctx.stroke();
      ctx.setLineDash([]);
      // + glyph
      const pcx = sx + slotW / 2;
      const pcy = sy + PRESET_H / 2;
      ctx.strokeStyle = "rgba(255,255,255,0.45)";
      ctx.lineWidth = 1;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(pcx - 3, pcy); ctx.lineTo(pcx + 3, pcy);
      ctx.moveTo(pcx, pcy - 3); ctx.lineTo(pcx, pcy + 3);
      ctx.stroke();
    }
  }
  ctx.textBaseline = "top"; // reset
  return cy + PRESET_H + 2;
}

/**
 * Hit-test the preset strip. Returns { slot, action } where action is
 * "load" (tap on filled body), "save" (tap on empty body), or "clear"
 * (tap on × corner of filled slot). Returns null if no hit.
 */
function hitPresetStrip(
  localX: number, localY: number,
  btnX: number, btnW: number, cy: number, ws: WardrobeState,
): { slot: number; action: "load" | "save" | "clear" } | null {
  if (localY < cy || localY >= cy + PRESET_H) return null;
  const slotGap = 3;
  const slotW = Math.floor((btnW - slotGap * (PRESET_COUNT - 1)) / PRESET_COUNT);
  for (let i = 0; i < PRESET_COUNT; i++) {
    const sx = btnX + i * (slotW + slotGap);
    if (localX < sx || localX >= sx + slotW) continue;
    const saved = ws.savedOutfits[i] ?? null;
    if (!saved) return { slot: i, action: "save" };
    // Check × corner — 8px hit box in the top-right
    const xHit = localX >= sx + slotW - 9 && localY <= cy + 9;
    return { slot: i, action: xHit ? "clear" : "load" };
  }
  return null;
}

/**
 * Calculate the rendered height of a palette grid for layout purposes.
 */
function paletteRowHeight(paletteLen: number, btnW: number): number {
  const availW = btnW - 56;
  const perRowAtMin = Math.floor(availW / (MIN_SWATCH + SWATCH_GAP));
  const cols = Math.min(paletteLen, perRowAtMin);
  const swatchSz = Math.min(MAX_SWATCH, Math.floor(availW / cols) - SWATCH_GAP);
  const rowCount = Math.ceil(paletteLen / cols);
  return rowCount * (swatchSz + SWATCH_GAP) + 4;
}

function drawFacePanel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  state: GameState,
): void {
  const ws = getState(state);
  const composerApp = toComposerAppearance();
  const pad = 6;
  const btnX = x + pad;
  const btnW = w - pad * 2;

  // ── Character Face Preview ──
  const previewH = Math.min(h * 0.35, 80);
  const previewCx = x + w / 2;
  const previewCy = y + pad + previewH * 0.45;

  const bgGrad = ctx.createRadialGradient(previewCx, previewCy, 0, previewCx, previewCy, previewH * 0.7);
  bgGrad.addColorStop(0, "rgba(40,30,50,0.4)");
  bgGrad.addColorStop(1, "rgba(20,16,24,0)");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(x, y, w, previewH + pad * 2);

  // Render head/face preview — zoomed in, front-facing
  const pCtx = getPreviewCtx();
  if (pCtx) {
    pCtx.clearRect(0, 0, PREVIEW_SIZE, PREVIEW_SIZE);
    pCtx.save();
    pCtx.translate(PREVIEW_SIZE / 2, PREVIEW_SIZE / 2 + 20);
    pCtx.scale(1.2, 1.2);
    const faceFrame = Math.floor(ws.elapsed * 2) % 4;  // subtle idle blink cycle
    renderLayerPreview(pCtx, composerApp, ["body", "eyes", "facial-hair", "makeup", "lips", "hair-front", "hair-back"], "s", faceFrame);
    pCtx.restore();
    const drawScale = previewH / PREVIEW_SIZE * 1.6;
    ctx.save();
    ctx.translate(previewCx, previewCy);
    ctx.scale(drawScale, drawScale);
    ctx.drawImage(_previewCanvas!, -PREVIEW_SIZE / 2, -PREVIEW_SIZE / 2);
    ctx.restore();
  }

  // Scrollable controls area
  const fControlsTop = y + previewH + pad * 2 + 2;
  const fControlsH = y + h - fControlsTop;
  const fTotalH = measureFaceControlsH(composerApp, btnW);
  ws.maxScroll = Math.max(0, fTotalH - fControlsH);
  ws.scrollOffset = Math.max(0, Math.min(ws.scrollOffset, ws.maxScroll));

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, fControlsTop, w, fControlsH);
  ctx.clip();

  let cy = fControlsTop - ws.scrollOffset;

  // ── Eye Shape ──
  cy = drawChipRow(ctx, "Eyes", EYE_SHAPE_LABELS, (composerApp as any).eyeStyle ?? 0, btnX, btnW, cy);
  cy += 2;

  // ── Eye Color ──
  cy = drawPaletteRow(ctx, "Eye Color", EYE_COLORS, (composerApp as any).eyeColor ?? 2, btnX, btnW, cy);
  cy += 2;

  // ── Expression ──
  cy = drawChipRow(ctx, "Express.", EXPRESSION_LABELS, EXPRESSION_VALUES.indexOf((composerApp as any).expression ?? "neutral"), btnX, btnW, cy);
  cy += 2;

  // ── Facial Hair ──
  cy = drawChipRow(ctx, "Beard", FACIAL_HAIR_LABELS, ((composerApp as any).facialHairStyle ?? -1) + 1, btnX, btnW, cy);
  cy += 2;

  // ── Facial Hair Color (if facial hair is not "None") ──
  if (((composerApp as any).facialHairStyle ?? -1) >= 0) {
    cy = drawPaletteRow(ctx, "Beard Clr", HAIR_COLORS, (composerApp as any).facialHairColor ?? 3, btnX, btnW, cy);
    cy += 2;
  }

  // ── Makeup ──
  cy = drawChipRow(ctx, "Makeup", MAKEUP_LABELS, ((composerApp as any).makeupStyle ?? -1) + 1, btnX, btnW, cy);
  cy += 2;

  // ── Makeup Color (if makeup is not "None") ──
  if (((composerApp as any).makeupStyle ?? -1) >= 0) {
    cy = drawPaletteRow(ctx, "Makeup Clr", MAKEUP_COLORS, (composerApp as any).makeupColor ?? 0, btnX, btnW, cy);
    cy += 2;
  }

  // ── Lips ──
  cy = drawChipRow(ctx, "Lips", LIP_LABELS, ((composerApp as any).lipStyle ?? -1) + 1, btnX, btnW, cy);
  cy += 2;

  // ── Lip Color (if lips are not "None") ──
  if (((composerApp as any).lipStyle ?? -1) >= 0) {
    cy = drawPaletteRow(ctx, "Lip Color", LIP_COLORS, (composerApp as any).lipColor ?? 0, btnX, btnW, cy);
  }

  ctx.restore(); // controls clip

  // Scroll indicator
  if (ws.maxScroll > 0) {
    const trackH = fControlsH - 8;
    const trackX2 = x + w - 3;
    const trackY2 = fControlsTop + 4;
    const thumbRatio = fControlsH / (fTotalH || 1);
    const thumbH = Math.max(12, trackH * thumbRatio);
    const scrollRatio = ws.scrollOffset / ws.maxScroll;
    const thumbY2 = trackY2 + scrollRatio * (trackH - thumbH);
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    drawRoundRect(ctx, trackX2, trackY2, 2.5, trackH, 1.25);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    drawRoundRect(ctx, trackX2, thumbY2, 2.5, thumbH, 1.25);
    ctx.fill();
  }

  ctx.textAlign = "left";
}

function measureFaceControlsH(composerApp: ComposerAppearance, btnW: number): number {
  let h = 0;
  h += chipRowH(EYE_SHAPE_LABELS.length);       // eye shape (8 items → multi-row)
  h += paletteRowHeight(EYE_COLORS.length, btnW) + 2; // eye color
  h += chipRowH(EXPRESSION_LABELS.length);       // expression (6 items → multi-row)
  h += chipRowH(FACIAL_HAIR_LABELS.length);      // facial hair (7 items → multi-row)
  if (((composerApp as any).facialHairStyle ?? -1) >= 0) {
    h += paletteRowHeight(HAIR_COLORS.length, btnW) + 2;
  }
  h += chipRowH(MAKEUP_LABELS.length);           // makeup (7 items → multi-row)
  if (((composerApp as any).makeupStyle ?? -1) >= 0) {
    h += paletteRowHeight(MAKEUP_COLORS.length, btnW) + 2;
  }
  h += chipRowH(LIP_LABELS.length);              // lips (6 items → multi-row)
  if (((composerApp as any).lipStyle ?? -1) >= 0) {
    h += paletteRowHeight(LIP_COLORS.length, btnW) + 2;
  }
  return h + 8;
}

function handleFacePanelTap(
  localX: number, localY: number,
  state: GameState,
  cr: { x: number; y: number; w: number; h: number },
): boolean {
  const composerApp = toComposerAppearance();
  const ws = getState(state);
  const pad = 6;
  const btnX = pad;
  const btnW = cr.w - pad * 2;
  const previewH = Math.min((cr.h - SUMMARY_H - TAB_BAR_H) * 0.35, 80);

  // Apply scroll offset
  const faceControlsTop = SUMMARY_H + TAB_BAR_H + previewH + pad * 2 + 2;
  if (localY < faceControlsTop || localY > cr.h) return false;

  let cy = faceControlsTop - ws.scrollOffset;

  // ── Eye Shape ──
  {
    const hit = hitChipRow(localX, localY, EYE_SHAPE_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { eyeStyle: hit } as any, "eye_style", "ui_equip");
      return true;
    }
    cy += chipRowH(EYE_SHAPE_LABELS.length);
  }

  // ── Eye Color ──
  {
    const hit = hitPaletteRow(localX, localY, EYE_COLORS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { eyeColor: hit } as any, "eye_color");
      return true;
    }
    cy += paletteRowHeight(EYE_COLORS.length, btnW) + 2;
  }

  // ── Expression ──
  {
    const hit = hitChipRow(localX, localY, EXPRESSION_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { expression: EXPRESSION_VALUES[hit] } as any, "expression", "ui_equip");
      return true;
    }
    cy += chipRowH(EXPRESSION_LABELS.length);
  }

  // ── Facial Hair ──
  {
    const hit = hitChipRow(localX, localY, FACIAL_HAIR_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { facialHairStyle: hit - 1 } as any, "facial_hair", "ui_equip");
      return true;
    }
    cy += chipRowH(FACIAL_HAIR_LABELS.length);
  }

  // ── Facial Hair Color ──
  if (((composerApp as any).facialHairStyle ?? -1) >= 0) {
    const hit = hitPaletteRow(localX, localY, HAIR_COLORS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { facialHairColor: hit } as any, "facial_hair_color");
      return true;
    }
    cy += paletteRowHeight(HAIR_COLORS.length, btnW) + 2;
  }

  // ── Makeup ──
  {
    const hit = hitChipRow(localX, localY, MAKEUP_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { makeupStyle: hit - 1 } as any, "makeup", "ui_equip");
      return true;
    }
    cy += chipRowH(MAKEUP_LABELS.length);
  }

  // ── Makeup Color ──
  if (((composerApp as any).makeupStyle ?? -1) >= 0) {
    const hit = hitPaletteRow(localX, localY, MAKEUP_COLORS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { makeupColor: hit } as any, "makeup_color");
      return true;
    }
    cy += paletteRowHeight(MAKEUP_COLORS.length, btnW) + 2;
  }

  // ── Lips ──
  {
    const hit = hitChipRow(localX, localY, LIP_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { lipStyle: hit - 1 } as any, "lip_style", "ui_equip");
      return true;
    }
    cy += chipRowH(LIP_LABELS.length);
  }

  // ── Lip Color ──
  if (((composerApp as any).lipStyle ?? -1) >= 0) {
    const hit = hitPaletteRow(localX, localY, LIP_COLORS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { lipColor: hit } as any, "lip_color");
      return true;
    }
  }

  return false;
}

// ─── Equipment Panel ────────────────────────────────────────────────────────

const RARITY_COLORS: Record<string, string> = {
  common: "#aaaaaa",
  uncommon: "#4a9a40",
  rare: "#3a7acc",
  legendary: "#c8a030",
  seasonal: "#cc5577",
};

const SLOT_ICONS: Record<EquipmentSlot, string> = {
  weapon: "⚔",
  armor: "🛡",
  accessory: "💎",
  shield: "🔰",
};

const SLOT_LABELS: Record<EquipmentSlot, string> = {
  weapon: "Weapon",
  armor: "Armor",
  accessory: "Accessory",
  shield: "Shield",
};

function drawEquipmentPanel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  state: GameState,
): void {
  const equipment = state.player.equipment;
  const combat = state.player.combat;
  const pad = 8;
  let cy = y + pad;

  // ── Combat Stats Summary ────────────────────────────────────────────
  ctx.fillStyle = C_BG_CARD;
  drawRoundRect(ctx, x + pad, cy, w - pad * 2, 52, 8);
  ctx.fill();

  ctx.font = "bold 11px sans-serif";
  ctx.fillStyle = C_ACCENT;
  ctx.textAlign = "left";
  ctx.fillText(`Lv.${combat.level}`, x + pad + 8, cy + 14);

  ctx.font = "10px sans-serif";
  ctx.fillStyle = C_TEXT_PRIMARY;
  ctx.fillText(`HP: ${combat.hp}/${combat.maxHp}`, x + pad + 50, cy + 14);

  // Attack and Defense with gear bonuses
  let gearAtk = 0, gearDef = 0, gearHp = 0;
  for (const slot of ["weapon", "armor", "accessory", "shield"] as const) {
    const item = equipment[slot];
    if (item?.equipmentData) {
      gearAtk += item.equipmentData.attackBonus;
      gearDef += item.equipmentData.defenseBonus;
      gearHp += item.equipmentData.hpBonus;
    }
  }

  ctx.fillStyle = "#ff8866";
  ctx.fillText(`ATK: ${combat.attack}`, x + pad + 8, cy + 30);
  if (gearAtk > 0) {
    ctx.fillStyle = "#4a9a40";
    ctx.fillText(`+${gearAtk}`, x + pad + 56, cy + 30);
  }

  ctx.fillStyle = "#66aaff";
  ctx.fillText(`DEF: ${combat.defense}`, x + pad + 8, cy + 44);
  if (gearDef > 0) {
    ctx.fillStyle = "#4a9a40";
    ctx.fillText(`+${gearDef}`, x + pad + 56, cy + 44);
  }

  if (gearHp > 0) {
    ctx.fillStyle = "#4a9a40";
    ctx.fillText(`+${gearHp} HP`, x + pad + 100, cy + 30);
  }

  // XP bar
  const xpBarX = x + pad + 8;
  const xpBarY = cy + 48;
  const xpBarW = w - pad * 2 - 16;
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  drawRoundRect(ctx, xpBarX, xpBarY - 2, xpBarW, 4, 2);
  ctx.fill();
  const xpFill = Math.min(1, combat.xp / (combat.xpToNext || 1));
  ctx.fillStyle = "#c8a030";
  drawRoundRect(ctx, xpBarX, xpBarY - 2, xpBarW * xpFill, 4, 2);
  ctx.fill();

  cy += 60;

  // ── Equipment Slots ─────────────────────────────────────────────────
  const slotH = 48;
  const slotGap = 4;
  const slots: EquipmentSlot[] = ["weapon", "armor", "shield", "accessory"];

  for (const slot of slots) {
    const item = equipment[slot];
    const slotY = cy;

    // Slot background
    ctx.fillStyle = item ? C_BG_CARD : "rgba(44,44,46,0.5)";
    drawRoundRect(ctx, x + pad, slotY, w - pad * 2, slotH, 8);
    ctx.fill();

    if (item) {
      // Rarity border
      const rarityColor = RARITY_COLORS[item.rarity] ?? "#aaa";
      ctx.strokeStyle = rarityColor;
      ctx.lineWidth = 1.5;
      drawRoundRect(ctx, x + pad, slotY, w - pad * 2, slotH, 8);
      ctx.stroke();

      // Slot icon
      ctx.font = "16px sans-serif";
      ctx.textAlign = "left";
      ctx.fillStyle = C_TEXT_PRIMARY;
      ctx.fillText(SLOT_ICONS[slot], x + pad + 8, slotY + 22);

      // Item name
      ctx.font = "bold 10px sans-serif";
      ctx.fillStyle = rarityColor;
      ctx.fillText(item.name, x + pad + 30, slotY + 16);

      // Stats
      ctx.font = "9px sans-serif";
      ctx.fillStyle = C_TEXT_SECONDARY;
      const ed = item.equipmentData!;
      const parts: string[] = [];
      if (ed.attackBonus > 0) parts.push(`+${ed.attackBonus} ATK`);
      if (ed.defenseBonus > 0) parts.push(`+${ed.defenseBonus} DEF`);
      if (ed.hpBonus > 0) parts.push(`+${ed.hpBonus} HP`);
      ctx.fillText(parts.join("  "), x + pad + 30, slotY + 30);

      // Description
      ctx.fillStyle = C_TEXT_DIM;
      ctx.font = "italic 8px sans-serif";
      const desc = ed.description.length > 35 ? ed.description.slice(0, 35) + "…" : ed.description;
      ctx.fillText(desc, x + pad + 30, slotY + 42);
    } else {
      // Empty slot
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = C_TEXT_DIM;
      ctx.lineWidth = 1;
      drawRoundRect(ctx, x + pad + 2, slotY + 2, w - pad * 2 - 4, slotH - 4, 6);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.font = "14px sans-serif";
      ctx.textAlign = "left";
      ctx.fillStyle = C_TEXT_DIM;
      ctx.fillText(SLOT_ICONS[slot], x + pad + 10, slotY + 24);

      ctx.font = "10px sans-serif";
      ctx.fillText(SLOT_LABELS[slot], x + pad + 32, slotY + 22);
      ctx.font = "8px sans-serif";
      ctx.fillText("No gear equipped", x + pad + 32, slotY + 34);
    }

    cy += slotH + slotGap;
  }

  cy += 4;

  // ── Inventory Equipment Items ───────────────────────────────────────
  const equipItems = state.player.inventory.filter(i => i.type === "equipment");
  if (equipItems.length > 0) {
    ctx.font = "bold 10px sans-serif";
    ctx.fillStyle = C_ACCENT;
    ctx.textAlign = "left";
    ctx.fillText("INVENTORY GEAR", x + pad + 4, cy + 10);
    cy += 16;

    for (const item of equipItems) {
      const ed = item.equipmentData!;
      const isEquipped = Object.values(equipment).some(e => e?.id === item.id);
      if (isEquipped) continue; // already shown above

      ctx.fillStyle = C_BG_CARD;
      drawRoundRect(ctx, x + pad, cy, w - pad * 2, 36, 6);
      ctx.fill();

      const rarityColor = RARITY_COLORS[item.rarity] ?? "#aaa";
      ctx.font = "bold 9px sans-serif";
      ctx.fillStyle = rarityColor;
      ctx.textAlign = "left";
      ctx.fillText(item.name, x + pad + 8, cy + 14);

      ctx.font = "8px sans-serif";
      ctx.fillStyle = C_TEXT_SECONDARY;
      const parts: string[] = [];
      if (ed.attackBonus > 0) parts.push(`+${ed.attackBonus} ATK`);
      if (ed.defenseBonus > 0) parts.push(`+${ed.defenseBonus} DEF`);
      if (ed.hpBonus > 0) parts.push(`+${ed.hpBonus} HP`);
      ctx.fillText(`${SLOT_LABELS[ed.slot]} — ${parts.join("  ")}`, x + pad + 8, cy + 26);

      // "Tap to equip" hint
      ctx.fillStyle = C_TEXT_DIM;
      ctx.textAlign = "right";
      ctx.fillText("Tap to equip", x + w - pad - 8, cy + 20);

      cy += 40;
    }
  }

  ctx.textAlign = "left"; // reset
}

/** Cache rects for gear slot tapping */
const _gearSlotRects: { slot: EquipmentSlot; y: number; h: number }[] = [];
const _gearInvRects: { itemId: string; y: number; h: number }[] = [];

function handleBodyPanelTap(
  localX: number, localY: number,
  state: GameState,
  cr: { x: number; y: number; w: number; h: number },
): boolean {
  const composerApp = toComposerAppearance();
  const pad = 6;
  const previewH = Math.min((cr.h - SUMMARY_H - TAB_BAR_H) * 0.45, 100);
  const btnW = cr.w - pad * 2;
  const btnH = 28;
  const btnX = pad;
  const isNude = composerApp.nude;

  // Get wardrobe state for slot selection and scroll
  const ws = getState(state);

  // ── Outfit slot taps — these are in the preview area, checked before scroll guard ──
  // Tapping a paper-doll slot selects it and opens the style/color editor at
  // the top of the scroll area. Tapping the same slot again deselects it.
  const slotRowH = 26;
  const slotColW = 28 + 4 * 2;
  const slotsTopY = SUMMARY_H + TAB_BAR_H + pad + 6;
  for (let si = 0; si < OUTFIT_SLOTS.length; si++) {
    const slot = OUTFIT_SLOTS[si];
    const sx = slot.ox < 0 ? pad : cr.w - pad - slotColW;
    const sy = slotsTopY + slot.oy * (slotRowH + 4);
    if (localX >= sx && localX < sx + slotColW && localY >= sy && localY < sy + slotRowH) {
      ws.selectedSlot = ws.selectedSlot === si ? -1 : si;
      ws.scrollOffset = 0;
      pressControl(ws, `slot_${slot.composerKey}`);
      playUiSound(state, "ui_click");
      return true;
    }
  }

  // ── Scrollable controls below preview — guard against taps in preview area ──
  const controlsTop = SUMMARY_H + TAB_BAR_H + previewH + pad * 2 + 4;
  const controlsBottom = cr.h;
  if (localY < controlsTop || localY > controlsBottom) {
    // Tap was in preview area — deselect slot if selected, otherwise rotate preview
    if (ws.selectedSlot >= 0) {
      ws.selectedSlot = -1;
      playUiSound(state, "ui_click");
      return true;
    }
    // Rotate: tap left half = counter-clockwise, right half = clockwise
    if (localX < cr.w / 2) {
      ws.previewDir = (ws.previewDir + 7) % 8;  // CCW
    } else {
      ws.previewDir = (ws.previewDir + 1) % 8;  // CW
    }
    playUiSound(state, "ui_click");
    return true;
  }

  let cy = controlsTop - ws.scrollOffset;

  // ── Selected Slot: Style Chip Row + Color Palette ──
  // Mirror of the draw code — section header, then style chips (for slots
  // that have a SLOT_STYLE_LABELS entry), then the color palette.
  {
    const selSlot = ws.selectedSlot >= 0 ? OUTFIT_SLOTS[ws.selectedSlot] : null;
    const selColorKey = selSlot ? SLOT_COLOR_KEY[selSlot.composerKey as string] : null;
    if (selSlot) {
      cy += 16; // section header
      const styleLabels = SLOT_STYLE_LABELS[selSlot.composerKey as string];
      if (styleLabels) {
        const hit = hitChipRow(localX, localY, styleLabels.length, btnX, btnW, cy);
        if (hit >= 0) {
          // Chip 0 is "None" → composer value -1
          pushHistory(ws);
          patchComposerAppearance({ [selSlot.composerKey]: hit - 1 } as any);
          pushEquipEffect(ws, localX, localY);
          pressControl(ws, `slotstyle_${hit}`);
          playUiSound(state, "ui_equip");
          return true;
        }
        cy += chipRowH(styleLabels.length) + 2;
      }
      if (selColorKey) {
        const palette = selSlot.composerKey === "hairStyle" ? HAIR_COLORS : ITEM_COLORS;
        const hit = hitPaletteRow(localX, localY, palette.length, btnX, btnW, cy);
        if (hit >= 0) {
          pushHistory(ws);
          patchComposerAppearance({ [selColorKey]: hit } as any);
          pushEquipEffect(ws, localX, localY);
          pressControl(ws, `slotcolor_${hit}`);
          playUiSound(state, "ui_click");
          return true;
        }
        cy += paletteRowHeight(palette.length, btnW) + 2;
      }
    }
  }

  // ── Presets taps ──
  {
    cy += 16; // section header
    const hit = hitPresetStrip(localX, localY, btnX, btnW, cy, ws);
    if (hit) {
      if (hit.action === "save") {
        // Snapshot current look into the empty slot
        ws.savedOutfits[hit.slot] = toComposerAppearance();
        pressControl(ws, `preset_save_${hit.slot}`);
        playUiSound(state, "ui_click");
      } else if (hit.action === "load") {
        const snap = ws.savedOutfits[hit.slot];
        if (snap) {
          pushHistory(ws);
          setComposerAppearanceFull(snap);
          pushEquipEffect(ws, localX, localY);
          pressControl(ws, `preset_load_${hit.slot}`);
          playUiSound(state, "ui_equip");
        }
      } else if (hit.action === "clear") {
        ws.savedOutfits[hit.slot] = null;
        pressControl(ws, `preset_clear_${hit.slot}`);
        playUiSound(state, "ui_click");
      }
      return true;
    }
    cy += PRESET_H + 2 + 4;
  }

  // ── Extras taps ──
  {
    cy += 16; // section header
    for (const extra of EXTRAS) {
      const styleVal = (composerApp[extra.styleKey] as number) ?? -1;
      {
        const hit = hitChipRow(localX, localY, extra.labels.length, btnX, btnW, cy);
        if (hit >= 0) {
          applyEdit(ws, state, localX, localY, { [extra.styleKey]: hit - 1 } as any, `extra_${String(extra.styleKey)}`, "ui_equip");
          return true;
        }
        cy += chipRowH(extra.labels.length);
      }
      if (extra.colorKey && styleVal >= 0) {
        const hit = hitPaletteRow(localX, localY, ITEM_COLORS.length, btnX, btnW, cy);
        if (hit >= 0) {
          applyEdit(ws, state, localX, localY, { [extra.colorKey]: hit } as any, `extra_color_${String(extra.colorKey)}`);
          return true;
        }
        cy += paletteRowHeight(ITEM_COLORS.length, btnW) + 2;
      }
    }
  }

  // ── Skin Tone palette ──
  {
    const hit = hitPaletteRow(localX, localY, SKIN_TONES.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { skinTone: hit } as any, "skin_tone");
      return true;
    }
    cy += paletteRowHeight(SKIN_TONES.length, btnW) + 1;
  }

  // ── Skin Detail chips ──
  {
    const hit = hitChipRow(localX, localY, SKIN_DETAIL_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { skinDetail: hit - 1 } as any, "skin_detail");
      return true;
    }
    cy += chipRowH(SKIN_DETAIL_LABELS.length);
  }

  // ── Build (bodyType) chips ──
  {
    const hit = hitChipRow(localX, localY, BODY_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { bodyType: hit } as any, "body_type");
      return true;
    }
    cy += chipRowH(BODY_LABELS.length);
  }

  // ── Build Archetype 1 chips ──
  {
    const hit = hitChipRow(localX, localY, ARCHETYPE_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { bodyArchetype1: ARCHETYPE_IDS[hit] } as any, "archetype1");
      return true;
    }
    cy += chipRowH(ARCHETYPE_LABELS.length);
  }

  // ── Build Archetype 2 chips ──
  {
    const hit = hitChipRow(localX, localY, ARCHETYPE_LABELS.length, btnX, btnW, cy);
    if (hit >= 0) {
      applyEdit(ws, state, localX, localY, { bodyArchetype2: ARCHETYPE_IDS[hit] } as any, "archetype2");
      return true;
    }
    cy += chipRowH(ARCHETYPE_LABELS.length);
  }

  // ── Blend + Body Scale sliders ──
  {
    const blendSliders: Array<{ key: string; max: number; isFractional?: boolean }> = [
      { key: "archetypeBlend", max: 4, isFractional: true },
      { key: "bodyScale", max: 4 },
    ];
    for (const slider of blendSliders) {
      if (localY >= cy && localY < cy + 18) {
        const trackX = btnX + 52;
        const trackW = btnW - 60;
        const relTrackX = localX - trackX;
        if (relTrackX >= 0 && relTrackX <= trackW) {
          const rawVal = Math.round((relTrackX / trackW) * slider.max);
          const clamped = Math.max(0, Math.min(slider.max, rawVal));
          const patchVal = slider.isFractional ? clamped / 4 : clamped;
          applyEdit(ws, state, localX, localY, { [slider.key]: patchVal } as any, `slider_${slider.key}`);
          return true;
        }
      }
      cy += 18;
    }
  }

  // ── Nude toggle button ──
  if (localY >= cy && localY < cy + btnH) {
    applyEdit(ws, state, localX, localY, { nude: !isNude } as any, "nude_toggle", "ui_equip");
    return true;
  }
  cy += btnH + 8;

  // ── Anatomy controls — always accessible ──
  const gender = composerApp.bodyGender ?? "androgynous";

  // Gender selector buttons
  const genderOpts = ["feminine", "masculine", "androgynous"];
  const optW = Math.floor((btnW - 4) / genderOpts.length);
  if (localY >= cy && localY < cy + 22) {
    const optIdx = Math.floor((localX - btnX) / (optW + 2));
    if (optIdx >= 0 && optIdx < genderOpts.length) {
      applyEdit(ws, state, localX, localY, { bodyGender: genderOpts[optIdx] } as any, `gender_${optIdx}`, "ui_equip");
      return true;
    }
  }
  cy += 28;

  // Body shape + sizing sliders — always accessible (body shape affects clothed silhouette)
  {
    const sliders: Array<{ key: string; max: number; show: boolean }> = [
      { key: "shoulderAdjust", max: 4, show: true },
      { key: "waistAdjust", max: 4, show: true },
      { key: "hipAdjust", max: 4, show: true },
      { key: "legGap", max: 4, show: true },
      { key: "breastSize", max: 4, show: gender === "feminine" || gender === "androgynous" },
      { key: "buttSize", max: 4, show: true },
      { key: "genitalSize", max: 4, show: isNude && gender === "masculine" },
    ];

    for (const slider of sliders) {
      if (!slider.show) continue;
      if (localY >= cy && localY < cy + 18) {
        const trackX = btnX + 52;
        const trackW = btnW - 60;
        const relTrackX = localX - trackX;
        if (relTrackX >= 0 && relTrackX <= trackW) {
          const newVal = Math.round((relTrackX / trackW) * slider.max);
          applyEdit(ws, state, localX, localY, { [slider.key]: Math.max(0, Math.min(slider.max, newVal)) } as any, `slider_${slider.key}`);
          return true;
        }
      }
      cy += 18;
    }
  }

  return false;
}

function handleGearTap(
  localX: number,
  localY: number,
  state: GameState,
  cr: { x: number; y: number; w: number; h: number },
): boolean {
  const ws = getState(state);
  const equipment = state.player.equipment;
  const pad = 8;
  const slotsStartY = SUMMARY_H + TAB_BAR_H + 4 + pad + 60;
  const slotH = 48;
  const slotGap = 4;
  const slots: EquipmentSlot[] = ["weapon", "armor", "shield", "accessory"];

  // Check equipment slot taps — unequip on tap
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const slotY = slotsStartY + i * (slotH + slotGap);
    if (localY >= slotY && localY < slotY + slotH) {
      const item = equipment[slot];
      if (item) {
        // Unequip — move back to inventory
        equipment[slot] = null;
        pushEquipEffect(ws, localX, localY);
        pressControl(ws, `gear_unequip_${slot}`);
        playUiSound(state, "ui_equip");
        return true;
      }
      return false;
    }
  }

  // Check inventory item taps — equip on tap
  const invStartY = slotsStartY + slots.length * (slotH + slotGap) + 20;
  const equipItems = state.player.inventory.filter(i =>
    i.type === "equipment" && !Object.values(equipment).some(e => e?.id === i.id)
  );

  for (let i = 0; i < equipItems.length; i++) {
    const itemY = invStartY + i * 40;
    if (localY >= itemY && localY < itemY + 36) {
      const item = equipItems[i];
      const ed = item.equipmentData;
      if (ed && state.player.combat.level >= ed.levelReq) {
        // Equip — set in the appropriate slot
        equipment[ed.slot] = item;
        pushEquipEffect(ws, localX, localY);
        pressControl(ws, `gear_equip_${ed.slot}`);
        playUiSound(state, "ui_equip");
        return true;
      }
      return false;
    }
  }

  return false;
}

// ─── Self-Register ──────────────────────────────────────────────────────────

registerApp(wardrobeApp);

export { wardrobeApp };
